/**
 * Elite Batch 2, Part 2 — Workflow Templates 73-100
 *
 * Production-grade prebuilt workflows: AI agents, app lifecycle,
 * appointment management, and writing/research assistants.
 *
 * DESIGN RULES (inherited from prebuiltWorkflows.ts):
 * 1. Every variable MUST come from a real API response field or AI-generated output
 * 2. No magic variables — if a condition checks something, an upstream node must produce it
 * 3. Webhook triggers accept raw data — the FIRST node after a webhook trigger MUST be
 *    an AI extraction node that reads {webhook_payload} and outputs named variables
 * 4. Every condition has a REAL value (never blank, never "?")
 * 5. Services are pre-selected — user never sees "pick a service"
 * 6. Output variable names use the actual API response field paths
 * 7. Trigger inputFields should be EMPTY for webhooks — the AI extraction node declares outputs
 * 8. AI prompts for extraction should say "find the X regardless of format" to handle variations
 */

function n(
  id: string,
  type: string,
  label: string,
  y: number,
  data: any = {},
  x = 80,
): any {
  return {
    id,
    type,
    position: { x, y },
    data: { nodeLabel: label, description: label, ...data },
  };
}
function e(source: string, target: string, handle = "output_1"): any {
  return {
    id: `${source}->${target}`,
    type: "custom",
    source,
    target,
    sourceHandle: handle,
  };
}
function trigger(
  label: string,
  triggerMode: "webhook_app" | "webhook_form" | "schedule" | "manual" | "email",
  config: {
    source?: string;
    event?: string;
    schedule?: string;
    howItStarts: string;
    setup: string;
    inputFields: Array<{ name: string; type: string; example?: string }>;
  },
): any {
  return n("1", "campaign", label, 40, {
    triggerType:
      triggerMode === "schedule"
        ? "schedule"
        : triggerMode === "email"
          ? "email"
          : "webhook",
    triggerMode,
    triggerSource: config.source,
    triggerEvent: config.event,
    scheduleDescription: config.schedule,
    howItStarts: config.howItStarts,
    setupInstructions: config.setup,
    inputFields: config.inputFields,
    description: config.howItStarts,
  });
}
function aiNode(
  id: string,
  label: string,
  y: number,
  systemPrompt: string,
  userPrompt: string,
  outputs: Array<{ field: string; as: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://api.openai.com/v1/chat/completions",
        headers: [
          { key: "Authorization", value: "Bearer {openai_key}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [
          { key: "model", value: "gpt-4o-mini" },
          {
            key: "messages",
            value: `[{"role":"system","content":"${systemPrompt}"},{"role":"user","content":"${userPrompt}"}]`,
          },
          { key: "response_format", value: '{"type":"json_object"}' },
        ],
        variables: [
          ...outputs.map((o) => ({
            name: `choices[0].message.content.${o.field}`,
            customName: o.as,
            type: o.type,
          })),
          {
            name: "usage.total_tokens",
            customName: "tokens_used",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}
function slackNode(
  id: string,
  label: string,
  y: number,
  message: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://hooks.slack.com/services/YOUR_WEBHOOK_URL",
        headers: [{ key: "Content-Type", value: "application/json" }],
        body: [{ key: "text", value: message }],
        variables: [{ name: "ok", customName: "slack_sent", type: "boolean" }],
      },
    },
    x,
  );
}
function emailNode(
  id: string,
  label: string,
  y: number,
  to: string,
  subject: string,
  body: string,
  x = 80,
): any {
  return n(
    id,
    "sendEmail",
    label,
    y,
    { sendEmailDetails: { to, content: { subject, body } } },
    x,
  );
}

// =============================================================================
// ELITE BATCH 2, PART 2 — WORKFLOWS 73-100
// =============================================================================

export const ELITE_BATCH2_PART2: Record<
  string,
  { nodes: any[]; edges: any[]; summary: string }
> = {
  // ===================================================================
  // 73. AI PRODUCT DESCRIPTION WRITER (8 nodes)
  // ===================================================================

  "AI Product Description Writer": {
    nodes: [
      trigger("Webhook: New product to describe", "webhook_app", {
        source: "Shopify / WooCommerce / PIM",
        event: "product.created",
        howItStarts:
          "A new product is added to your catalog or submitted for description generation",
        setup:
          "In Shopify -> Settings -> Notifications -> Webhooks -> Add webhook -> Product creation, point to your workflow URL. Or POST product data via API.",
        inputFields: [],
      }),

      // 2. Extract product details from webhook
      aiNode(
        "2",
        "AI extract product details",
        280,
        "Read this product webhook payload and extract the product details regardless of format. Find the product name, category, key features, target audience, brand name, and price. Respond JSON: {product_name: string, category: string, features: string, target_audience: string, brand_name: string, price: string, raw_description: string}",
        "{webhook_payload}",
        [
          { field: "product_name", as: "product_name", type: "string" },
          { field: "category", as: "category", type: "string" },
          { field: "features", as: "features", type: "string" },
          { field: "target_audience", as: "target_audience", type: "string" },
          { field: "brand_name", as: "brand_name", type: "string" },
          { field: "price", as: "price", type: "string" },
          { field: "raw_description", as: "raw_description", type: "string" },
        ],
      ),

      // 3. AI generate SEO-optimized product description
      aiNode(
        "3",
        "AI write SEO product description",
        520,
        "You are an expert e-commerce copywriter. Write a compelling, SEO-optimized product description. Include a headline, short description (under 160 chars for meta), long description with benefits and features, and 5 SEO keywords. Match the brand voice. Respond JSON: {headline: string, meta_description: string, long_description: string, bullet_points: string, seo_keywords: string, tone_used: string}",
        "Product: {product_name}, Category: {category}, Features: {features}, Target: {target_audience}, Brand: {brand_name}, Price: {price}, Raw: {raw_description}",
        [
          { field: "headline", as: "headline", type: "string" },
          { field: "meta_description", as: "meta_description", type: "string" },
          { field: "long_description", as: "long_description", type: "string" },
          { field: "bullet_points", as: "bullet_points", type: "string" },
          { field: "seo_keywords", as: "seo_keywords", type: "string" },
          { field: "tone_used", as: "tone_used", type: "string" },
        ],
      ),

      // 4. Quality check condition
      n("4", "condition", "Description length adequate? (> 50 chars)", 760, {
        conditionData: [
          {
            variable: "long_description",
            value: "50",
            comparison_operator: "LENGTH GREATER THAN",
            logical_operator: "AND",
          },
        ],
      }),

      // 5. Push to Shopify product
      n("5", "hitApi", "Update product in Shopify", 1000, {
        hitApiDetails: {
          method: "PUT",
          url: "https://{shopify_store}.myshopify.com/admin/api/2024-01/products/{product_id}.json",
          headers: [
            { key: "X-Shopify-Access-Token", value: "{shopify_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "product",
              value:
                '{"title":"{headline}","body_html":"{long_description}","metafields_global_description_tag":"{meta_description}","tags":"{seo_keywords}"}',
            },
          ],
          variables: [
            {
              name: "product.id",
              customName: "updated_product_id",
              type: "string",
            },
          ],
        },
      }),

      // 6. Notify team on Slack
      slackNode(
        "6",
        "Notify team: description published",
        1240,
        "Product description published for *{product_name}*\\nHeadline: {headline}\\nSEO Keywords: {seo_keywords}\\nTone: {tone_used}",
      ),

      // 7. Approval gate for failed quality check
      n(
        "7",
        "approvalGate",
        "Manual review: short description",
        1000,
        {
          approvalGateData: {
            message:
              "Product description for {product_name} failed quality check (too short).\\n\\nGenerated headline: {headline}\\nDescription: {long_description}\\n\\nApprove to publish anyway, or reject to regenerate.",
          },
        },
        460,
      ),

      // 8. Log description to Google Sheets for tracking
      n("8", "hitApi", "Log to Google Sheets", 1480, {
        hitApiDetails: {
          method: "POST",
          url: "https://sheets.googleapis.com/v4/spreadsheets/{sheet_id}/values/Descriptions!A1:append?valueInputOption=USER_ENTERED",
          headers: [
            { key: "Authorization", value: "Bearer {google_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "values",
              value:
                '[["{product_name}","{headline}","{meta_description}","{seo_keywords}","{tone_used}","{price}"]]',
            },
          ],
          variables: [
            {
              name: "updates.updatedRows",
              customName: "rows_logged",
              type: "number",
            },
          ],
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"), // pass quality -> update Shopify
      e("4", "7", "output_2"), // fail quality -> manual review
      e("5", "6"),
      e("6", "8"),
      e("7", "5"), // approved -> update Shopify
    ],
    summary: `**AI Product Description Writer** — Automatically generates SEO-optimized product descriptions from raw catalog data.\n\n1. Product webhook fires from Shopify/WooCommerce/PIM\n2. **AI extracts** product name, features, category, target audience from payload\n3. **AI writes** headline, meta description, long description, bullet points, and SEO keywords\n4. Quality gate checks description length\n5. **Shopify API** updates product listing with generated copy\n6. **Slack notification** confirms publication\n7. Short descriptions routed to **human review** for approval\n8. All descriptions logged to **Google Sheets** for tracking\n\n**To activate:** Say "connect openai", "connect shopify", "connect slack", "connect google sheets"`,
  },

  // ===================================================================
  // 74. AI PRODUCT REVIEW ANALYZER (8 nodes)
  // ===================================================================

  "AI Product Review Analyzer": {
    nodes: [
      trigger("Schedule: Analyze new reviews daily", "schedule", {
        schedule: "Every day at 9:00 AM",
        howItStarts:
          "Runs daily to fetch and analyze new product reviews from your store",
        setup:
          "Activate the workflow. Connect your review platform (Trustpilot, Yotpo, or Shopify reviews API). The AI will analyze sentiment and extract actionable insights.",
        inputFields: [
          { name: "product_id", type: "string", example: "prod_12345" },
        ],
      }),

      // 2. Fetch recent reviews from Trustpilot
      n("2", "hitApi", "Fetch reviews from Trustpilot", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.trustpilot.com/v1/business-units/{business_unit_id}/reviews?perPage=20&orderBy=createdat.desc",
          headers: [{ key: "apikey", value: "{trustpilot_key}" }],
          variables: [
            { name: "reviews", customName: "reviews_raw", type: "string" },
            {
              name: "reviews.length",
              customName: "review_count",
              type: "number",
            },
          ],
        },
      }),

      // 3. AI sentiment and theme analysis
      aiNode(
        "3",
        "AI analyze review sentiment and themes",
        520,
        "You are a product review analyst. Analyze these customer reviews and identify: overall sentiment distribution, top 3 positive themes, top 3 negative themes, common feature requests, and urgency level for product team. Respond JSON: {avg_sentiment_score: number, positive_pct: number, negative_pct: number, neutral_pct: number, top_positive_themes: string, top_negative_themes: string, feature_requests: string, urgency: string, summary: string, action_items: string}",
        "Reviews: {reviews_raw}",
        [
          {
            field: "avg_sentiment_score",
            as: "avg_sentiment_score",
            type: "number",
          },
          { field: "positive_pct", as: "positive_pct", type: "number" },
          { field: "negative_pct", as: "negative_pct", type: "number" },
          {
            field: "top_positive_themes",
            as: "top_positive_themes",
            type: "string",
          },
          {
            field: "top_negative_themes",
            as: "top_negative_themes",
            type: "string",
          },
          { field: "feature_requests", as: "feature_requests", type: "string" },
          { field: "urgency", as: "urgency", type: "string" },
          { field: "summary", as: "review_summary", type: "string" },
          { field: "action_items", as: "action_items", type: "string" },
        ],
      ),

      // 4. Check for negative sentiment spike
      n("4", "condition", "Negative reviews > 30%?", 760, {
        conditionData: [
          {
            variable: "negative_pct",
            value: "30",
            comparison_operator: "GREATER THAN",
            logical_operator: "AND",
          },
        ],
      }),

      // 5. Alert product team on Slack (critical)
      slackNode(
        "5",
        "Alert #product: negative spike detected",
        1000,
        "WARNING: Negative review spike detected!\\nNegative: {negative_pct}%\\nTop issues: {top_negative_themes}\\nUrgency: {urgency}\\nAction items: {action_items}",
      ),

      // 6. Approval gate for product team response
      n("6", "approvalGate", "Product team reviews insights", 1240, {
        approvalGateData: {
          message:
            "Review analysis complete.\\n\\nSentiment: {avg_sentiment_score}/10 | Positive: {positive_pct}% | Negative: {negative_pct}%\\nPositive themes: {top_positive_themes}\\nNegative themes: {top_negative_themes}\\nFeature requests: {feature_requests}\\n\\nApprove to push insights to Jira and share report.",
        },
      }),

      // 7. Create Jira tickets for action items
      n("7", "hitApi", "Create Jira tickets for action items", 1480, {
        hitApiDetails: {
          method: "POST",
          url: "https://{jira_domain}.atlassian.net/rest/api/3/issue",
          headers: [
            { key: "Authorization", value: "Basic {jira_auth}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "fields",
              value:
                '{"project":{"key":"{jira_project_key}"},"summary":"Review Insights: {top_negative_themes}","description":{"type":"doc","version":1,"content":[{"type":"paragraph","content":[{"type":"text","text":"Sentiment: {avg_sentiment_score}/10. Action items: {action_items}. Feature requests: {feature_requests}"}]}]},"issuetype":{"name":"Task"},"priority":{"name":"High"}}',
            },
          ],
          variables: [
            { name: "id", customName: "jira_ticket_id", type: "string" },
            { name: "key", customName: "jira_ticket_key", type: "string" },
          ],
        },
      }),

      // 8. Email weekly review digest to stakeholders
      emailNode(
        "8",
        "Send review digest to stakeholders",
        1720,
        "{stakeholder_email}",
        "Weekly Review Digest: {avg_sentiment_score}/10 sentiment",
        "<h2>Product Review Analysis</h2><p><strong>Sentiment Score:</strong> {avg_sentiment_score}/10</p><p><strong>Positive:</strong> {positive_pct}% | <strong>Negative:</strong> {negative_pct}%</p><p><strong>Top Positive Themes:</strong> {top_positive_themes}</p><p><strong>Top Negative Themes:</strong> {top_negative_themes}</p><p><strong>Feature Requests:</strong> {feature_requests}</p><p><strong>Action Items:</strong> {action_items}</p><p><strong>Jira Ticket:</strong> {jira_ticket_key}</p>",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"), // negative spike -> alert
      e("4", "6", "output_2"), // normal -> approval gate
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary: `**AI Product Review Analyzer** — Daily AI-powered review analysis with sentiment tracking and automated action items.\n\n1. Scheduled daily fetch of new reviews from Trustpilot\n2. **AI analyzes** sentiment distribution, positive/negative themes, feature requests\n3. Negative spike detection (>30%) triggers **Slack alert** to product team\n4. **Human approval** before pushing insights to task management\n5. **Jira tickets** auto-created for action items\n6. **Email digest** sent to stakeholders with full analysis\n\n**To activate:** Say "connect openai", "connect trustpilot", "connect slack", "connect jira", "connect email"`,
  },

  // ===================================================================
  // 75. AI PROOFREADER (7 nodes)
  // ===================================================================

  "AI Proofreader": {
    nodes: [
      trigger("Webhook: Document submitted for proofreading", "webhook_form", {
        source: "Google Docs / CMS / Form",
        event: "document.submitted",
        howItStarts:
          "A document or text block is submitted for AI proofreading via form or webhook",
        setup:
          "Connect your CMS or create a submission form. POST the document text to your workflow URL. The AI will proofread grammar, style, clarity, and tone.",
        inputFields: [],
      }),

      // 2. Extract document content
      aiNode(
        "2",
        "AI extract document content",
        280,
        "Read this webhook payload and extract the document content to proofread. Find the text content, author name, document title, and desired tone regardless of field naming. Respond JSON: {document_text: string, author_name: string, document_title: string, desired_tone: string}",
        "{webhook_payload}",
        [
          { field: "document_text", as: "document_text", type: "string" },
          { field: "author_name", as: "author_name", type: "string" },
          { field: "document_title", as: "document_title", type: "string" },
          { field: "desired_tone", as: "desired_tone", type: "string" },
        ],
      ),

      // 3. AI proofread: grammar, style, clarity
      aiNode(
        "3",
        "AI proofread: grammar, style, clarity",
        520,
        "You are a professional proofreader and editor. Proofread this text thoroughly for grammar, spelling, punctuation, style, clarity, and readability. Provide the corrected text, a list of changes made, a readability score (1-10), and overall quality assessment. Keep the author voice intact. Respond JSON: {corrected_text: string, changes_list: string, change_count: number, readability_score: number, quality_assessment: string, tone_match: string}",
        "Title: {document_title}\\nDesired tone: {desired_tone}\\nText:\\n{document_text}",
        [
          { field: "corrected_text", as: "corrected_text", type: "string" },
          { field: "changes_list", as: "changes_list", type: "string" },
          { field: "change_count", as: "change_count", type: "number" },
          {
            field: "readability_score",
            as: "readability_score",
            type: "number",
          },
          {
            field: "quality_assessment",
            as: "quality_assessment",
            type: "string",
          },
          { field: "tone_match", as: "tone_match", type: "string" },
        ],
      ),

      // 4. Check if many changes needed
      n("4", "condition", "More than 10 changes needed?", 760, {
        conditionData: [
          {
            variable: "change_count",
            value: "10",
            comparison_operator: "GREATER THAN",
            logical_operator: "AND",
          },
        ],
      }),

      // 5. Approval gate for heavy edits
      n("5", "approvalGate", "Editor reviews heavy edits", 1000, {
        approvalGateData: {
          message:
            "Document \\'{document_title}\\' by {author_name} requires {change_count} changes.\\n\\nQuality: {quality_assessment}\\nReadability: {readability_score}/10\\nChanges: {changes_list}\\n\\nApprove to send corrected version to author.",
        },
      }),

      // 6. Email corrected document to author
      emailNode(
        "6",
        "Send proofread document to author",
        1240,
        "{author_email}",
        "Proofread: {document_title} - {change_count} corrections",
        "<h2>Proofread Complete: {document_title}</h2><p><strong>Readability Score:</strong> {readability_score}/10</p><p><strong>Changes Made:</strong> {change_count}</p><p><strong>Quality Assessment:</strong> {quality_assessment}</p><p><strong>Tone Match:</strong> {tone_match}</p><hr><h3>Changes List</h3><p>{changes_list}</p><hr><h3>Corrected Text</h3><p>{corrected_text}</p>",
      ),

      // 7. Log proofreading to Google Sheets
      n("7", "hitApi", "Log to tracking sheet", 1480, {
        hitApiDetails: {
          method: "POST",
          url: "https://sheets.googleapis.com/v4/spreadsheets/{sheet_id}/values/ProofreadLog!A1:append?valueInputOption=USER_ENTERED",
          headers: [
            { key: "Authorization", value: "Bearer {google_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "values",
              value:
                '[["{document_title}","{author_name}","{change_count}","{readability_score}","{quality_assessment}"]]',
            },
          ],
          variables: [
            {
              name: "updates.updatedRows",
              customName: "rows_logged",
              type: "number",
            },
          ],
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"), // heavy edits -> approval
      e("4", "6", "output_2"), // light edits -> send directly
      e("5", "6"),
      e("6", "7"),
    ],
    summary: `**AI Proofreader** — Professional AI proofreading with grammar, style, clarity checks and tracked corrections.\n\n1. Document submitted via webhook or form\n2. **AI extracts** content, title, author, desired tone\n3. **AI proofreads** for grammar, spelling, punctuation, style, clarity, readability\n4. Heavy edits (>10 changes) routed to **human editor review**\n5. Corrected document **emailed to author** with change summary\n6. All proofreading sessions **logged to Google Sheets**\n\n**To activate:** Say "connect openai", "connect email", "connect google sheets"`,
  },

  // ===================================================================
  // 76. AI RESEARCH ASSISTANT (8 nodes)
  // ===================================================================

  "AI Research Assistant": {
    nodes: [
      trigger("Webhook: Research request submitted", "webhook_form", {
        source: "Slack / Form / Internal Tool",
        event: "research.requested",
        howItStarts:
          "A research request is submitted via form, Slack command, or internal tool",
        setup:
          "Connect a Slack slash command, form submission, or internal tool to POST research requests to your workflow URL. Include the topic and any context.",
        inputFields: [],
      }),

      // 2. Extract research request
      aiNode(
        "2",
        "AI extract research request",
        280,
        "Read this research request payload and extract the research topic, context, requester name, urgency, desired output format, and any specific questions. Respond JSON: {topic: string, context: string, requester_name: string, urgency: string, output_format: string, specific_questions: string}",
        "{webhook_payload}",
        [
          { field: "topic", as: "topic", type: "string" },
          { field: "context", as: "context", type: "string" },
          { field: "requester_name", as: "requester_name", type: "string" },
          { field: "urgency", as: "urgency", type: "string" },
          { field: "output_format", as: "output_format", type: "string" },
          {
            field: "specific_questions",
            as: "specific_questions",
            type: "string",
          },
        ],
      ),

      // 3. Fetch related data from Serper (web search)
      n("3", "hitApi", "Web search via Serper API", 520, {
        hitApiDetails: {
          method: "POST",
          url: "https://google.serper.dev/search",
          headers: [
            { key: "X-API-KEY", value: "{serper_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "q", value: "{topic} {specific_questions}" },
            { key: "num", value: "10" },
          ],
          variables: [
            { name: "organic", customName: "search_results", type: "string" },
            {
              name: "knowledgeGraph.description",
              customName: "kg_description",
              type: "string",
            },
          ],
        },
      }),

      // 4. AI synthesize research report
      aiNode(
        "4",
        "AI synthesize research report",
        760,
        "You are an expert research analyst. Synthesize the search results and context into a comprehensive research brief. Answer the specific questions. Include key findings, data points, sources, and recommendations. Format as requested. Respond JSON: {executive_summary: string, key_findings: string, data_points: string, sources_cited: string, recommendations: string, confidence_level: string, gaps_identified: string}",
        "Topic: {topic}\\nContext: {context}\\nQuestions: {specific_questions}\\nFormat: {output_format}\\nSearch results: {search_results}\\nKnowledge graph: {kg_description}",
        [
          {
            field: "executive_summary",
            as: "executive_summary",
            type: "string",
          },
          { field: "key_findings", as: "key_findings", type: "string" },
          { field: "data_points", as: "data_points", type: "string" },
          { field: "sources_cited", as: "sources_cited", type: "string" },
          { field: "recommendations", as: "recommendations", type: "string" },
          { field: "confidence_level", as: "confidence_level", type: "string" },
          { field: "gaps_identified", as: "gaps_identified", type: "string" },
        ],
      ),

      // 5. Check urgency level
      n("5", "condition", "Is urgency high or critical?", 1000, {
        conditionData: [
          {
            variable: "urgency",
            value: "high",
            comparison_operator: "EQUAL TO",
            logical_operator: "OR",
          },
          {
            variable: "urgency",
            value: "critical",
            comparison_operator: "EQUAL TO",
            logical_operator: "OR",
          },
        ],
      }),

      // 6. Slack notify requester immediately
      slackNode(
        "6",
        "Slack DM: urgent research ready",
        1240,
        "Research report ready for *{requester_name}* (URGENT)\\nTopic: {topic}\\nConfidence: {confidence_level}\\n\\nExecutive Summary: {executive_summary}\\n\\nKey Findings: {key_findings}",
      ),

      // 7. Email full research report
      emailNode(
        "7",
        "Email research report",
        1240,
        "{requester_email}",
        "Research Report: {topic}",
        "<h2>Research Report: {topic}</h2><p><strong>Confidence:</strong> {confidence_level}</p><h3>Executive Summary</h3><p>{executive_summary}</p><h3>Key Findings</h3><p>{key_findings}</p><h3>Data Points</h3><p>{data_points}</p><h3>Recommendations</h3><p>{recommendations}</p><h3>Gaps Identified</h3><p>{gaps_identified}</p><h3>Sources</h3><p>{sources_cited}</p>",
        460,
      ),

      // 8. Store research in Notion
      n("8", "hitApi", "Save to Notion research database", 1480, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.notion.com/v1/pages",
          headers: [
            { key: "Authorization", value: "Bearer {notion_key}" },
            { key: "Notion-Version", value: "2022-06-28" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "parent", value: '{"database_id":"{notion_db_id}"}' },
            {
              key: "properties",
              value:
                '{"Topic":{"title":[{"text":{"content":"{topic}"}}]},"Requester":{"rich_text":[{"text":{"content":"{requester_name}"}}]},"Confidence":{"select":{"name":"{confidence_level}"}},"Urgency":{"select":{"name":"{urgency}"}}}',
            },
          ],
          variables: [
            { name: "id", customName: "notion_page_id", type: "string" },
          ],
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"), // urgent -> Slack DM immediately
      e("5", "7", "output_2"), // normal -> email report
      e("6", "7"),
      e("7", "8"),
    ],
    summary: `**AI Research Assistant** — Automated research pipeline with web search, AI synthesis, and multi-channel delivery.\n\n1. Research request arrives via Slack, form, or internal tool\n2. **AI extracts** topic, context, specific questions, urgency\n3. **Serper API** fetches top 10 web results\n4. **AI synthesizes** executive summary, key findings, data points, recommendations\n5. Urgent requests trigger **Slack DM** for immediate delivery\n6. Full report **emailed** to requester\n7. Research archived to **Notion database** for team knowledge base\n\n**To activate:** Say "connect openai", "connect serper", "connect slack", "connect email", "connect notion"`,
  },

  // ===================================================================
  // 77. AI RESEARCH ASSISTANT PRO (8 nodes)
  // ===================================================================

  "AI Research Assistant Pro": {
    nodes: [
      trigger("Webhook: Pro research request", "webhook_form", {
        source: "Internal Tool / API",
        event: "research.pro.requested",
        howItStarts:
          "A deep research request is submitted requiring multi-source analysis and competitive intelligence",
        setup:
          "POST research requests to your workflow URL. Include topic, competitor names, industry, and depth level (quick/standard/deep).",
        inputFields: [],
      }),

      // 2. Extract research parameters
      aiNode(
        "2",
        "AI parse research parameters",
        280,
        "Read this research request and extract: topic, industry, competitors to analyze, depth level, time range, and any specific data requests. Respond JSON: {topic: string, industry: string, competitors: string, depth_level: string, time_range: string, data_requests: string, requester_email: string}",
        "{webhook_payload}",
        [
          { field: "topic", as: "topic", type: "string" },
          { field: "industry", as: "industry", type: "string" },
          { field: "competitors", as: "competitors", type: "string" },
          { field: "depth_level", as: "depth_level", type: "string" },
          { field: "time_range", as: "time_range", type: "string" },
          { field: "data_requests", as: "data_requests", type: "string" },
          { field: "requester_email", as: "requester_email", type: "string" },
        ],
      ),

      // 3. Multi-source data gathering via Serper + news
      n("3", "hitApi", "Serper: industry + competitor search", 520, {
        hitApiDetails: {
          method: "POST",
          url: "https://google.serper.dev/search",
          headers: [
            { key: "X-API-KEY", value: "{serper_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "q",
              value:
                "{topic} {industry} {competitors} market analysis {time_range}",
            },
            { key: "num", value: "15" },
          ],
          variables: [
            { name: "organic", customName: "web_results", type: "string" },
            { name: "news", customName: "news_results", type: "string" },
          ],
        },
      }),

      // 4. AI deep analysis with competitive intelligence
      aiNode(
        "4",
        "AI deep analysis + competitive intel",
        760,
        "You are a senior research analyst with expertise in competitive intelligence. Produce a comprehensive analysis covering: market overview, competitive landscape, SWOT for each competitor, trend analysis, threat assessment, opportunity mapping, and strategic recommendations. Use data-driven insights. Respond JSON: {market_overview: string, competitive_landscape: string, swot_analysis: string, trend_analysis: string, threat_assessment: string, opportunities: string, strategic_recommendations: string, data_confidence: string}",
        "Topic: {topic}\\nIndustry: {industry}\\nCompetitors: {competitors}\\nDepth: {depth_level}\\nTime range: {time_range}\\nSpecific requests: {data_requests}\\nWeb results: {web_results}\\nNews: {news_results}",
        [
          { field: "market_overview", as: "market_overview", type: "string" },
          {
            field: "competitive_landscape",
            as: "competitive_landscape",
            type: "string",
          },
          { field: "swot_analysis", as: "swot_analysis", type: "string" },
          { field: "trend_analysis", as: "trend_analysis", type: "string" },
          {
            field: "threat_assessment",
            as: "threat_assessment",
            type: "string",
          },
          { field: "opportunities", as: "opportunities", type: "string" },
          {
            field: "strategic_recommendations",
            as: "strategic_recommendations",
            type: "string",
          },
          { field: "data_confidence", as: "data_confidence", type: "string" },
        ],
      ),

      // 5. Approval gate for senior review
      n("5", "approvalGate", "Senior analyst reviews report", 1000, {
        approvalGateData: {
          message:
            "Pro Research Report for: {topic}\\nIndustry: {industry} | Competitors: {competitors}\\nConfidence: {data_confidence}\\n\\nMarket Overview: {market_overview}\\n\\nThreat Assessment: {threat_assessment}\\n\\nApprove to distribute report.",
        },
      }),

      // 6. Store in Notion knowledge base
      n("6", "hitApi", "Store in Notion knowledge base", 1240, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.notion.com/v1/pages",
          headers: [
            { key: "Authorization", value: "Bearer {notion_key}" },
            { key: "Notion-Version", value: "2022-06-28" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "parent", value: '{"database_id":"{notion_research_db}"}' },
            {
              key: "properties",
              value:
                '{"Title":{"title":[{"text":{"content":"Pro Research: {topic}"}}]},"Industry":{"select":{"name":"{industry}"}},"Depth":{"select":{"name":"{depth_level}"}},"Confidence":{"rich_text":[{"text":{"content":"{data_confidence}"}}]}}',
            },
          ],
          variables: [
            { name: "id", customName: "notion_page_id", type: "string" },
          ],
        },
      }),

      // 7. Email comprehensive report
      emailNode(
        "7",
        "Deliver pro research report",
        1480,
        "{requester_email}",
        "Pro Research: {topic} - Competitive Intelligence Report",
        "<h1>Pro Research Report: {topic}</h1><p><em>Industry: {industry} | Confidence: {data_confidence}</em></p><h2>Market Overview</h2><p>{market_overview}</p><h2>Competitive Landscape</h2><p>{competitive_landscape}</p><h2>SWOT Analysis</h2><p>{swot_analysis}</p><h2>Trend Analysis</h2><p>{trend_analysis}</p><h2>Threat Assessment</h2><p>{threat_assessment}</p><h2>Opportunities</h2><p>{opportunities}</p><h2>Strategic Recommendations</h2><p>{strategic_recommendations}</p>",
      ),

      // 8. Notify team on Slack
      slackNode(
        "8",
        "Notify team: pro report published",
        1720,
        "Pro Research Report published for *{topic}*\\nIndustry: {industry}\\nConfidence: {data_confidence}\\nRequester: {requester_email}\\nNotion page: {notion_page_id}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary: `**AI Research Assistant Pro** — Deep competitive intelligence with multi-source analysis and senior review.\n\n1. Pro research request submitted with topic, competitors, industry\n2. **AI extracts** parameters including depth level and time range\n3. **Serper API** gathers web results and news across multiple queries\n4. **AI produces** market overview, competitive landscape, SWOT, trend analysis, threat assessment\n5. **Senior analyst approval** before distribution\n6. Report stored in **Notion knowledge base**\n7. Comprehensive report **emailed** to requester\n8. **Slack notification** to team with Notion link\n\n**To activate:** Say "connect openai", "connect serper", "connect notion", "connect email", "connect slack"`,
  },

  // ===================================================================
  // 78. AI RESUME SCREENING AGENT (9 nodes)
  // ===================================================================

  "AI Resume Screening Agent": {
    nodes: [
      trigger("Webhook: Resume submitted via ATS", "webhook_app", {
        source: "Greenhouse / Lever / Workable",
        event: "candidate.applied",
        howItStarts:
          "A candidate submits their resume through your ATS or careers page",
        setup:
          "In your ATS (Greenhouse, Lever, Workable) go to Integrations -> Webhooks -> Add endpoint -> Candidate Application, point to your workflow URL.",
        inputFields: [],
      }),

      // 2. Extract resume data from ATS webhook
      aiNode(
        "2",
        "AI extract resume data",
        280,
        "Read this ATS webhook payload and extract: candidate name, email, phone, job title applied for, resume text or URL, years of experience, education, skills, and cover letter if present. Different ATS systems send different formats. Respond JSON: {candidate_name: string, candidate_email: string, job_title: string, resume_text: string, years_experience: string, education: string, skills: string, cover_letter: string}",
        "{webhook_payload}",
        [
          { field: "candidate_name", as: "candidate_name", type: "string" },
          { field: "candidate_email", as: "candidate_email", type: "string" },
          { field: "job_title", as: "job_title", type: "string" },
          { field: "resume_text", as: "resume_text", type: "string" },
          { field: "years_experience", as: "years_experience", type: "string" },
          { field: "education", as: "education", type: "string" },
          { field: "skills", as: "skills", type: "string" },
          { field: "cover_letter", as: "cover_letter", type: "string" },
        ],
      ),

      // 3. Fetch job requirements from internal API
      n("3", "hitApi", "Fetch job requirements", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://{ats_domain}/api/v1/jobs/{job_id}/requirements",
          headers: [{ key: "Authorization", value: "Bearer {ats_token}" }],
          variables: [
            {
              name: "requirements",
              customName: "job_requirements",
              type: "string",
            },
            {
              name: "must_have_skills",
              customName: "must_have_skills",
              type: "string",
            },
            {
              name: "nice_to_have_skills",
              customName: "nice_to_have_skills",
              type: "string",
            },
            {
              name: "min_experience_years",
              customName: "min_experience_years",
              type: "number",
            },
          ],
        },
      }),

      // 4. AI score and screen resume
      aiNode(
        "4",
        "AI score resume against requirements",
        760,
        "You are an expert HR screener. Score this resume against the job requirements on: skills match (0-30), experience fit (0-25), education match (0-20), culture indicators (0-15), presentation quality (0-10). Total is 0-100. Be fair and unbiased. Flag any red flags or exceptional strengths. Respond JSON: {total_score: number, skills_score: number, experience_score: number, education_score: number, culture_score: number, presentation_score: number, recommendation: string, red_flags: string, strengths: string, interview_questions: string}",
        "Resume: {resume_text}\\nSkills: {skills}\\nExperience: {years_experience}\\nEducation: {education}\\nJob: {job_title}\\nRequirements: {job_requirements}\\nMust-have: {must_have_skills}\\nNice-to-have: {nice_to_have_skills}\\nMin experience: {min_experience_years}\\nCover letter: {cover_letter}",
        [
          { field: "total_score", as: "total_score", type: "number" },
          { field: "skills_score", as: "skills_score", type: "number" },
          { field: "experience_score", as: "experience_score", type: "number" },
          { field: "recommendation", as: "recommendation", type: "string" },
          { field: "red_flags", as: "red_flags", type: "string" },
          { field: "strengths", as: "strengths", type: "string" },
          {
            field: "interview_questions",
            as: "interview_questions",
            type: "string",
          },
        ],
      ),

      // 5. Score threshold check
      n("5", "condition", "Score >= 70 (qualified)?", 1000, {
        conditionData: [
          {
            variable: "total_score",
            value: "70",
            comparison_operator: "GREATER THAN OR EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 6. Recruiter approval for qualified candidates
      n("6", "approvalGate", "Recruiter reviews top candidate", 1240, {
        approvalGateData: {
          message:
            "Candidate: {candidate_name} for {job_title}\\nScore: {total_score}/100 (Skills: {skills_score}, Experience: {experience_score})\\nStrengths: {strengths}\\nRed flags: {red_flags}\\nRecommendation: {recommendation}\\n\\nSuggested interview questions:\\n{interview_questions}\\n\\nApprove to advance to interview.",
        },
      }),

      // 7. Advance candidate in ATS
      n("7", "hitApi", "Advance candidate in ATS", 1480, {
        hitApiDetails: {
          method: "POST",
          url: "https://{ats_domain}/api/v1/candidates/{candidate_id}/advance",
          headers: [
            { key: "Authorization", value: "Bearer {ats_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "stage", value: "interview" },
            {
              key: "notes",
              value:
                "AI Score: {total_score}/100. {recommendation}. Strengths: {strengths}",
            },
          ],
          variables: [
            {
              name: "candidate.stage",
              customName: "new_stage",
              type: "string",
            },
          ],
        },
      }),

      // 8. Notify hiring manager via Slack
      slackNode(
        "8",
        "Notify hiring manager: candidate ready",
        1720,
        "New qualified candidate for *{job_title}*\\nCandidate: {candidate_name} ({candidate_email})\\nScore: {total_score}/100\\nStrengths: {strengths}\\nInterview questions prepared.",
      ),

      // 9. Auto-reject email for low scores
      emailNode(
        "9",
        "Send rejection email (low score)",
        1240,
        "{candidate_email}",
        "Update on your {job_title} application",
        "<p>Dear {candidate_name},</p><p>Thank you for your interest in the {job_title} position. After careful review of your application, we have decided to move forward with other candidates whose qualifications more closely match our current requirements.</p><p>We encourage you to apply for future positions that match your skills and experience.</p><p>Best regards,<br>Hiring Team</p>",
        460,
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"), // qualified -> recruiter review
      e("5", "9", "output_2"), // not qualified -> rejection
      e("6", "7"),
      e("7", "8"),
    ],
    summary: `**AI Resume Screening Agent** — Automated resume scoring with bias-free analysis and ATS integration.\n\n1. Candidate applies via ATS (Greenhouse, Lever, Workable)\n2. **AI extracts** resume details from ATS webhook payload\n3. **Job requirements fetched** from ATS API\n4. **AI scores** resume: skills (30), experience (25), education (20), culture (15), presentation (10)\n5. Score >= 70 routed to **recruiter approval** with interview questions\n6. Approved candidates **advanced in ATS** to interview stage\n7. **Slack notification** to hiring manager\n8. Low scorers receive **courteous rejection email**\n\n**To activate:** Say "connect openai", "connect greenhouse/lever", "connect slack", "connect email"`,
  },

  // ===================================================================
  // 79. AI SALES ROLEPLAY COACH (8 nodes)
  // ===================================================================

  "AI Sales Roleplay Coach": {
    nodes: [
      trigger("Webhook: Rep requests roleplay session", "webhook_form", {
        source: "Slack / Internal Tool",
        event: "roleplay.requested",
        howItStarts:
          "A sales rep requests a roleplay coaching session, providing their upcoming call scenario",
        setup:
          "Create a Slack slash command or internal form that POSTs to your workflow URL with the rep's name, deal details, and scenario type.",
        inputFields: [],
      }),

      // 2. Extract scenario details
      aiNode(
        "2",
        "AI extract roleplay scenario",
        280,
        "Read this roleplay request and extract: rep name, rep email, deal context, prospect persona to simulate, objection type to practice, and product being sold. Respond JSON: {rep_name: string, rep_email: string, deal_context: string, prospect_persona: string, objection_type: string, product_name: string, difficulty_level: string}",
        "{webhook_payload}",
        [
          { field: "rep_name", as: "rep_name", type: "string" },
          { field: "rep_email", as: "rep_email", type: "string" },
          { field: "deal_context", as: "deal_context", type: "string" },
          { field: "prospect_persona", as: "prospect_persona", type: "string" },
          { field: "objection_type", as: "objection_type", type: "string" },
          { field: "product_name", as: "product_name", type: "string" },
          { field: "difficulty_level", as: "difficulty_level", type: "string" },
        ],
      ),

      // 3. AI generate roleplay scenario + objections
      aiNode(
        "3",
        "AI generate roleplay scenario",
        520,
        "You are a sales training expert. Create a realistic roleplay scenario for this rep to practice. Generate: an opening statement from the simulated prospect, 3 realistic objections they would raise, the ideal response framework for each objection, and scoring criteria. Match the difficulty level. Respond JSON: {scenario_opening: string, objection_1: string, ideal_response_1: string, objection_2: string, ideal_response_2: string, objection_3: string, ideal_response_3: string, scoring_rubric: string, tips: string}",
        "Rep: {rep_name}\\nDeal: {deal_context}\\nProspect persona: {prospect_persona}\\nObjection type: {objection_type}\\nProduct: {product_name}\\nDifficulty: {difficulty_level}",
        [
          { field: "scenario_opening", as: "scenario_opening", type: "string" },
          { field: "objection_1", as: "objection_1", type: "string" },
          { field: "ideal_response_1", as: "ideal_response_1", type: "string" },
          { field: "objection_2", as: "objection_2", type: "string" },
          { field: "ideal_response_2", as: "ideal_response_2", type: "string" },
          { field: "objection_3", as: "objection_3", type: "string" },
          { field: "ideal_response_3", as: "ideal_response_3", type: "string" },
          { field: "scoring_rubric", as: "scoring_rubric", type: "string" },
          { field: "tips", as: "tips", type: "string" },
        ],
      ),

      // 4. Send scenario to rep via email
      emailNode(
        "4",
        "Send roleplay scenario to rep",
        760,
        "{rep_email}",
        "Sales Roleplay: {prospect_persona} - {objection_type}",
        "<h2>Sales Roleplay Session</h2><p><strong>Prospect Persona:</strong> {prospect_persona}</p><p><strong>Product:</strong> {product_name}</p><p><strong>Difficulty:</strong> {difficulty_level}</p><hr><h3>Scenario Opening</h3><p><em>{scenario_opening}</em></p><h3>Objection 1: {objection_type}</h3><p><strong>Prospect says:</strong> {objection_1}</p><p><strong>Ideal response:</strong> {ideal_response_1}</p><h3>Objection 2</h3><p><strong>Prospect says:</strong> {objection_2}</p><p><strong>Ideal response:</strong> {ideal_response_2}</p><h3>Objection 3</h3><p><strong>Prospect says:</strong> {objection_3}</p><p><strong>Ideal response:</strong> {ideal_response_3}</p><h3>Scoring Rubric</h3><p>{scoring_rubric}</p><h3>Tips</h3><p>{tips}</p>",
      ),

      // 5. Wait for rep to practice (delay)
      n("5", "delayAction", "Wait 1 day for practice", 1000, {
        delayAction: { time: 1, timeFormat: "days" },
      }),

      // 6. AI generate follow-up coaching tips
      aiNode(
        "6",
        "AI generate personalized coaching",
        1240,
        "Based on this sales scenario, generate advanced coaching tips personalized for this rep. Include: common mistakes for this objection type, power phrases to use, body language tips for video calls, and a 5-minute warm-up routine for the actual call. Respond JSON: {common_mistakes: string, power_phrases: string, video_call_tips: string, warmup_routine: string, confidence_booster: string}",
        "Rep: {rep_name}\\nObjection type: {objection_type}\\nProduct: {product_name}\\nDifficulty: {difficulty_level}\\nProspect: {prospect_persona}",
        [
          { field: "common_mistakes", as: "common_mistakes", type: "string" },
          { field: "power_phrases", as: "power_phrases", type: "string" },
          { field: "video_call_tips", as: "video_call_tips", type: "string" },
          { field: "warmup_routine", as: "warmup_routine", type: "string" },
          {
            field: "confidence_booster",
            as: "confidence_booster",
            type: "string",
          },
        ],
      ),

      // 7. Send coaching follow-up email
      emailNode(
        "7",
        "Send coaching follow-up to rep",
        1480,
        "{rep_email}",
        "Pre-Call Coaching: {product_name} - {prospect_persona}",
        "<h2>Pre-Call Coaching for {rep_name}</h2><h3>Common Mistakes to Avoid</h3><p>{common_mistakes}</p><h3>Power Phrases</h3><p>{power_phrases}</p><h3>Video Call Tips</h3><p>{video_call_tips}</p><h3>5-Minute Warm-Up Routine</h3><p>{warmup_routine}</p><h3>Confidence Booster</h3><p>{confidence_booster}</p><p><em>Good luck on your call!</em></p>",
      ),

      // 8. Log session to Google Sheets
      n("8", "hitApi", "Log coaching session", 1720, {
        hitApiDetails: {
          method: "POST",
          url: "https://sheets.googleapis.com/v4/spreadsheets/{sheet_id}/values/Coaching!A1:append?valueInputOption=USER_ENTERED",
          headers: [
            { key: "Authorization", value: "Bearer {google_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "values",
              value:
                '[["{rep_name}","{objection_type}","{prospect_persona}","{product_name}","{difficulty_level}"]]',
            },
          ],
          variables: [
            {
              name: "updates.updatedRows",
              customName: "rows_logged",
              type: "number",
            },
          ],
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary: `**AI Sales Roleplay Coach** — Personalized sales practice scenarios with objection handling and follow-up coaching.\n\n1. Rep requests roleplay session with deal context and objection type\n2. **AI generates** realistic prospect persona, 3 tailored objections, ideal responses\n3. Scenario **emailed to rep** with scoring rubric and tips\n4. **1-day delay** for practice time\n5. **AI generates** advanced coaching: common mistakes, power phrases, video tips, warm-up routine\n6. Follow-up coaching **emailed** before the real call\n7. Session **logged to Google Sheets** for tracking rep development\n\n**To activate:** Say "connect openai", "connect email", "connect google sheets"`,
  },

  // ===================================================================
  // 80. AI SOCIAL LISTENER (8 nodes)
  // ===================================================================

  "AI Social Listener": {
    nodes: [
      trigger("Schedule: Scan social mentions hourly", "schedule", {
        schedule: "Every hour",
        howItStarts:
          "Runs hourly to scan social media and web for brand mentions and sentiment shifts",
        setup:
          "Activate the workflow and configure your brand keywords and competitor names. Connect your social listening API (Mention, Brand24, or Twitter API).",
        inputFields: [
          {
            name: "brand_keywords",
            type: "string",
            example: "OmniEngage, omniengage",
          },
          {
            name: "competitor_keywords",
            type: "string",
            example: "HubSpot, Mailchimp, ActiveCampaign",
          },
        ],
      }),

      // 2. Fetch mentions from Mention API
      n("2", "hitApi", "Fetch mentions from Mention API", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://web.mention.com/api/accounts/{mention_account_id}/alerts/{alert_id}/mentions?limit=50&since_id={last_mention_id}",
          headers: [{ key: "Authorization", value: "Bearer {mention_key}" }],
          variables: [
            { name: "mentions", customName: "mentions_raw", type: "string" },
            {
              name: "mentions.length",
              customName: "mention_count",
              type: "number",
            },
          ],
        },
      }),

      // 3. AI analyze sentiment and categorize
      aiNode(
        "3",
        "AI analyze mention sentiment",
        520,
        "You are a social listening analyst. Analyze these brand mentions and categorize each by: sentiment (positive/negative/neutral), source platform, urgency (requires_response/monitor/informational), topic category, and competitive context. Summarize overall brand health. Respond JSON: {total_mentions: number, positive_count: number, negative_count: number, urgent_mentions: string, top_topics: string, competitive_mentions: string, brand_health_score: number, recommended_responses: string, trending_sentiment: string}",
        "Brand: {brand_keywords}\\nCompetitors: {competitor_keywords}\\nMentions: {mentions_raw}",
        [
          { field: "total_mentions", as: "total_mentions", type: "number" },
          { field: "positive_count", as: "positive_count", type: "number" },
          { field: "negative_count", as: "negative_count", type: "number" },
          { field: "urgent_mentions", as: "urgent_mentions", type: "string" },
          { field: "top_topics", as: "top_topics", type: "string" },
          {
            field: "competitive_mentions",
            as: "competitive_mentions",
            type: "string",
          },
          {
            field: "brand_health_score",
            as: "brand_health_score",
            type: "number",
          },
          {
            field: "recommended_responses",
            as: "recommended_responses",
            type: "string",
          },
          {
            field: "trending_sentiment",
            as: "trending_sentiment",
            type: "string",
          },
        ],
      ),

      // 4. Check for urgent mentions
      n("4", "condition", "Any urgent mentions needing response?", 760, {
        conditionData: [
          {
            variable: "negative_count",
            value: "3",
            comparison_operator: "GREATER THAN",
            logical_operator: "AND",
          },
        ],
      }),

      // 5. Alert team on Slack (urgent)
      slackNode(
        "5",
        "Alert #social: urgent mentions detected",
        1000,
        "ALERT: {negative_count} negative mentions detected!\\nBrand health: {brand_health_score}/100\\nUrgent: {urgent_mentions}\\nTopics: {top_topics}\\nRecommended responses: {recommended_responses}",
      ),

      // 6. Log analysis to Google Sheets
      n(
        "6",
        "hitApi",
        "Log to social monitoring sheet",
        1000,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://sheets.googleapis.com/v4/spreadsheets/{sheet_id}/values/SocialLog!A1:append?valueInputOption=USER_ENTERED",
            headers: [
              { key: "Authorization", value: "Bearer {google_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              {
                key: "values",
                value:
                  '[["{total_mentions}","{positive_count}","{negative_count}","{brand_health_score}","{trending_sentiment}","{top_topics}"]]',
              },
            ],
            variables: [
              {
                name: "updates.updatedRows",
                customName: "rows_logged",
                type: "number",
              },
            ],
          },
        },
        460,
      ),

      // 7. Check for competitive mention patterns
      n("7", "condition", "Competitive mentions detected?", 1240, {
        conditionData: [
          {
            variable: "competitive_mentions",
            value: "",
            comparison_operator: "NOT EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 8. Email competitive intelligence digest
      emailNode(
        "8",
        "Email competitive intel digest",
        1480,
        "{marketing_lead_email}",
        "Competitive Intelligence: {competitor_keywords} mentions detected",
        "<h2>Competitive Intelligence Alert</h2><p><strong>Brand Health:</strong> {brand_health_score}/100 | <strong>Sentiment Trend:</strong> {trending_sentiment}</p><h3>Competitive Mentions</h3><p>{competitive_mentions}</p><h3>Top Topics</h3><p>{top_topics}</p><h3>Recommended Responses</h3><p>{recommended_responses}</p>",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"), // urgent -> Slack alert
      e("4", "6", "output_2"), // normal -> log
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_1"), // competitive mentions -> email digest
    ],
    summary: `**AI Social Listener** — Hourly brand monitoring with sentiment analysis and competitive intelligence.\n\n1. Runs **every hour** to scan social mentions\n2. **Mention API** fetches latest brand and competitor mentions\n3. **AI analyzes** sentiment, urgency, topics, competitive context, brand health score\n4. Urgent negative spikes trigger **Slack alert** with recommended responses\n5. All analysis **logged to Google Sheets** for trend tracking\n6. Competitive mentions trigger **email intelligence digest** to marketing lead\n\n**To activate:** Say "connect openai", "connect mention", "connect slack", "connect google sheets", "connect email"`,
  },

  // ===================================================================
  // 81. AI SOCIAL MEDIA ENGAGEMENT AGENT (9 nodes)
  // ===================================================================

  "AI Social Media Engagement Agent": {
    nodes: [
      trigger("Webhook: New social media mention/comment", "webhook_app", {
        source: "Twitter / Instagram / LinkedIn",
        event: "mention.created",
        howItStarts:
          "A new mention, comment, or DM is received on your social media channels",
        setup:
          "Connect your social media management tool (Buffer, Hootsuite, or direct platform APIs) to POST new mentions/comments to your workflow URL.",
        inputFields: [],
      }),

      // 2. Extract mention details
      aiNode(
        "2",
        "AI extract social mention details",
        280,
        "Read this social media webhook payload and extract: platform, author username, author follower count, message text, mention type (comment/mention/DM/reply), post URL, and any hashtags. Respond JSON: {platform: string, author_username: string, follower_count: number, message_text: string, mention_type: string, post_url: string, hashtags: string}",
        "{webhook_payload}",
        [
          { field: "platform", as: "platform", type: "string" },
          { field: "author_username", as: "author_username", type: "string" },
          { field: "follower_count", as: "follower_count", type: "number" },
          { field: "message_text", as: "message_text", type: "string" },
          { field: "mention_type", as: "mention_type", type: "string" },
          { field: "post_url", as: "post_url", type: "string" },
          { field: "hashtags", as: "hashtags", type: "string" },
        ],
      ),

      // 3. AI classify intent and craft response
      aiNode(
        "3",
        "AI classify intent + draft response",
        520,
        "You are a social media community manager. Analyze this mention and: classify the intent (question/complaint/praise/feature_request/spam), determine if response is needed, draft an on-brand response matching platform tone (casual for Twitter, professional for LinkedIn), and flag VIP accounts (>10k followers). Respond JSON: {intent: string, needs_response: string, response_draft: string, is_vip: string, sentiment: string, priority: string, escalation_needed: string}",
        "Platform: {platform}\\nFrom: @{author_username} ({follower_count} followers)\\nType: {mention_type}\\nMessage: {message_text}\\nHashtags: {hashtags}",
        [
          { field: "intent", as: "intent", type: "string" },
          { field: "needs_response", as: "needs_response", type: "string" },
          { field: "response_draft", as: "response_draft", type: "string" },
          { field: "is_vip", as: "is_vip", type: "string" },
          { field: "sentiment", as: "sentiment", type: "string" },
          { field: "priority", as: "priority", type: "string" },
          {
            field: "escalation_needed",
            as: "escalation_needed",
            type: "string",
          },
        ],
      ),

      // 4. Check if response needed
      n("4", "condition", "Response needed?", 760, {
        conditionData: [
          {
            variable: "needs_response",
            value: "true",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 5. Check if VIP or escalation needed
      n("5", "condition", "VIP or escalation needed?", 1000, {
        conditionData: [
          {
            variable: "is_vip",
            value: "true",
            comparison_operator: "EQUAL TO",
            logical_operator: "OR",
          },
          {
            variable: "escalation_needed",
            value: "true",
            comparison_operator: "EQUAL TO",
            logical_operator: "OR",
          },
        ],
      }),

      // 6. Approval gate for VIP/escalation
      n("6", "approvalGate", "Community manager reviews VIP response", 1240, {
        approvalGateData: {
          message:
            "VIP/Escalation on {platform}\\nFrom: @{author_username} ({follower_count} followers)\\nIntent: {intent} | Sentiment: {sentiment}\\nMessage: {message_text}\\n\\nDrafted response: {response_draft}\\n\\nApprove to post response.",
        },
      }),

      // 7. Post response via social API
      n("7", "hitApi", "Post response via social API", 1480, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.buffer.com/1/updates/create.json",
          headers: [
            { key: "Authorization", value: "Bearer {buffer_token}" },
            { key: "Content-Type", value: "application/x-www-form-urlencoded" },
          ],
          body: [
            { key: "profile_ids[]", value: "{profile_id}" },
            { key: "text", value: "@{author_username} {response_draft}" },
            { key: "now", value: "true" },
          ],
          variables: [
            { name: "success", customName: "response_posted", type: "boolean" },
            { name: "updates[0].id", customName: "post_id", type: "string" },
          ],
        },
      }),

      // 8. Notify team on Slack
      slackNode(
        "8",
        "Log engagement to #social-responses",
        1720,
        "Social response posted on {platform}\\nTo: @{author_username} | Intent: {intent} | Sentiment: {sentiment}\\nOriginal: {message_text}\\nResponse: {response_draft}\\nVIP: {is_vip}",
      ),

      // 9. Log to Google Sheets for analytics
      n("9", "hitApi", "Log engagement metrics", 1960, {
        hitApiDetails: {
          method: "POST",
          url: "https://sheets.googleapis.com/v4/spreadsheets/{sheet_id}/values/Engagement!A1:append?valueInputOption=USER_ENTERED",
          headers: [
            { key: "Authorization", value: "Bearer {google_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "values",
              value:
                '[["{platform}","{author_username}","{follower_count}","{intent}","{sentiment}","{is_vip}","{priority}"]]',
            },
          ],
          variables: [
            {
              name: "updates.updatedRows",
              customName: "rows_logged",
              type: "number",
            },
          ],
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"), // needs response -> check VIP
      e("4", "9", "output_2"), // no response needed -> just log
      e("5", "6", "output_1"), // VIP -> approval gate
      e("5", "7", "output_2"), // normal -> auto-post
      e("6", "7"),
      e("7", "8"),
      e("8", "9"),
    ],
    summary: `**AI Social Media Engagement Agent** — Intelligent social response automation with VIP detection and approval gates.\n\n1. Social mention/comment arrives from any platform\n2. **AI extracts** platform, author, follower count, message, mention type\n3. **AI classifies** intent, sentiment, drafts on-brand response, flags VIPs\n4. No-response mentions (spam, etc.) are just **logged**\n5. VIP accounts (>10k followers) or escalations require **community manager approval**\n6. Response **posted via Buffer** API\n7. All engagements **logged to Slack** and **Google Sheets**\n\n**To activate:** Say "connect openai", "connect buffer", "connect slack", "connect google sheets"`,
  },

  // ===================================================================
  // 82. AI SPREADSHEET ANALYST (7 nodes)
  // ===================================================================

  "AI Spreadsheet Analyst": {
    nodes: [
      trigger("Webhook: Spreadsheet analysis requested", "webhook_form", {
        source: "Google Sheets / Internal Tool",
        event: "analysis.requested",
        howItStarts:
          "A user submits a spreadsheet or data range for AI-powered analysis",
        setup:
          "POST the spreadsheet ID and range to your workflow URL, or use a Google Sheets trigger. The AI will fetch the data, analyze it, and return insights.",
        inputFields: [],
      }),

      // 2. Extract request parameters
      aiNode(
        "2",
        "AI extract analysis request",
        280,
        "Read this analysis request and extract: Google Sheet ID, data range, analysis type (summary/trends/anomalies/forecast), specific questions about the data, and requester email. Respond JSON: {sheet_id: string, data_range: string, analysis_type: string, questions: string, requester_email: string}",
        "{webhook_payload}",
        [
          { field: "sheet_id", as: "sheet_id", type: "string" },
          { field: "data_range", as: "data_range", type: "string" },
          { field: "analysis_type", as: "analysis_type", type: "string" },
          { field: "questions", as: "questions", type: "string" },
          { field: "requester_email", as: "requester_email", type: "string" },
        ],
      ),

      // 3. Fetch data from Google Sheets
      n("3", "hitApi", "Fetch data from Google Sheets", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://sheets.googleapis.com/v4/spreadsheets/{sheet_id}/values/{data_range}",
          headers: [{ key: "Authorization", value: "Bearer {google_token}" }],
          variables: [
            { name: "values", customName: "spreadsheet_data", type: "string" },
            { name: "range", customName: "actual_range", type: "string" },
          ],
        },
      }),

      // 4. AI analyze spreadsheet data
      aiNode(
        "4",
        "AI analyze spreadsheet data",
        760,
        "You are a data analyst. Analyze this spreadsheet data and provide: executive summary, key metrics (min, max, avg, median, totals), trends identified, anomalies or outliers, answers to specific questions, and actionable recommendations. Format numbers clearly. Respond JSON: {executive_summary: string, key_metrics: string, trends: string, anomalies: string, answers: string, recommendations: string, data_quality_score: number, row_count: number}",
        "Data range: {actual_range}\\nAnalysis type: {analysis_type}\\nQuestions: {questions}\\nData:\\n{spreadsheet_data}",
        [
          {
            field: "executive_summary",
            as: "executive_summary",
            type: "string",
          },
          { field: "key_metrics", as: "key_metrics", type: "string" },
          { field: "trends", as: "trends", type: "string" },
          { field: "anomalies", as: "anomalies", type: "string" },
          { field: "answers", as: "answers", type: "string" },
          { field: "recommendations", as: "recommendations", type: "string" },
          {
            field: "data_quality_score",
            as: "data_quality_score",
            type: "number",
          },
        ],
      ),

      // 5. Write analysis results back to sheet
      n("5", "hitApi", "Write analysis to new sheet tab", 1000, {
        hitApiDetails: {
          method: "POST",
          url: "https://sheets.googleapis.com/v4/spreadsheets/{sheet_id}/values/AI_Analysis!A1:append?valueInputOption=USER_ENTERED",
          headers: [
            { key: "Authorization", value: "Bearer {google_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "values",
              value:
                '[["Analysis Type","{analysis_type}"],["Summary","{executive_summary}"],["Key Metrics","{key_metrics}"],["Trends","{trends}"],["Anomalies","{anomalies}"],["Recommendations","{recommendations}"],["Data Quality","{data_quality_score}/10"]]',
            },
          ],
          variables: [
            {
              name: "updates.updatedRows",
              customName: "rows_written",
              type: "number",
            },
          ],
        },
      }),

      // 6. Email analysis report
      emailNode(
        "6",
        "Email analysis report",
        1240,
        "{requester_email}",
        "Spreadsheet Analysis: {analysis_type} - {actual_range}",
        "<h2>Spreadsheet Analysis Report</h2><p><strong>Range:</strong> {actual_range} | <strong>Type:</strong> {analysis_type} | <strong>Data Quality:</strong> {data_quality_score}/10</p><h3>Executive Summary</h3><p>{executive_summary}</p><h3>Key Metrics</h3><p>{key_metrics}</p><h3>Trends</h3><p>{trends}</p><h3>Anomalies</h3><p>{anomalies}</p><h3>Your Questions Answered</h3><p>{answers}</p><h3>Recommendations</h3><p>{recommendations}</p>",
      ),

      // 7. Notify via Slack
      slackNode(
        "7",
        "Notify: analysis complete",
        1480,
        "Spreadsheet analysis complete for {requester_email}\\nRange: {actual_range}\\nType: {analysis_type}\\nData quality: {data_quality_score}/10\\nKey finding: {executive_summary}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
    summary: `**AI Spreadsheet Analyst** — AI-powered data analysis for Google Sheets with automated reporting.\n\n1. Analysis request submitted with sheet ID, range, and questions\n2. **Google Sheets API** fetches the data\n3. **AI analyzes** metrics, trends, anomalies, answers specific questions\n4. Analysis results **written back** to a new tab in the sheet\n5. Full report **emailed** to requester\n6. Completion **notified via Slack**\n\n**To activate:** Say "connect openai", "connect google sheets", "connect email", "connect slack"`,
  },

  // ===================================================================
  // 83. AI SUMMARIZER BOT (7 nodes)
  // ===================================================================

  "AI Summarizer Bot": {
    nodes: [
      trigger("Webhook: Content submitted for summarization", "webhook_form", {
        source: "Slack / API / Form",
        event: "content.submitted",
        howItStarts:
          "Long-form content (article, report, transcript) is submitted for AI summarization",
        setup:
          "POST content to your workflow URL via Slack slash command, form, or API. Include the text and desired summary length.",
        inputFields: [],
      }),

      // 2. Extract content
      aiNode(
        "2",
        "AI extract content to summarize",
        280,
        "Read this payload and extract: the full text to summarize, desired summary length (brief/standard/detailed), output format (bullet_points/paragraph/executive_brief), source type (article/report/transcript/email_thread), and requester info. Respond JSON: {full_text: string, summary_length: string, output_format: string, source_type: string, requester_name: string, requester_email: string}",
        "{webhook_payload}",
        [
          { field: "full_text", as: "full_text", type: "string" },
          { field: "summary_length", as: "summary_length", type: "string" },
          { field: "output_format", as: "output_format", type: "string" },
          { field: "source_type", as: "source_type", type: "string" },
          { field: "requester_name", as: "requester_name", type: "string" },
          { field: "requester_email", as: "requester_email", type: "string" },
        ],
      ),

      // 3. AI generate summary
      aiNode(
        "3",
        "AI generate multi-level summary",
        520,
        "You are an expert summarizer. Create three summary levels: a one-line TLDR, a concise summary (3-5 sentences), and a detailed summary with key takeaways. Extract action items if any. Identify the main topics. Respond JSON: {tldr: string, concise_summary: string, detailed_summary: string, key_takeaways: string, action_items: string, main_topics: string, word_count_original: number, word_count_summary: number}",
        "Source type: {source_type}\\nDesired length: {summary_length}\\nFormat: {output_format}\\nContent:\\n{full_text}",
        [
          { field: "tldr", as: "tldr", type: "string" },
          { field: "concise_summary", as: "concise_summary", type: "string" },
          { field: "detailed_summary", as: "detailed_summary", type: "string" },
          { field: "key_takeaways", as: "key_takeaways", type: "string" },
          { field: "action_items", as: "action_items", type: "string" },
          { field: "main_topics", as: "main_topics", type: "string" },
          {
            field: "word_count_original",
            as: "word_count_original",
            type: "number",
          },
          {
            field: "word_count_summary",
            as: "word_count_summary",
            type: "number",
          },
        ],
      ),

      // 4. Check if action items exist
      n("4", "condition", "Action items found?", 760, {
        conditionData: [
          {
            variable: "action_items",
            value: "none",
            comparison_operator: "NOT EQUAL TO",
            logical_operator: "AND",
          },
          {
            variable: "action_items",
            value: "",
            comparison_operator: "NOT EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 5. Slack post with action items highlighted
      slackNode(
        "5",
        "Post summary + action items to Slack",
        1000,
        "Summary for {requester_name} ({source_type})\\n\\nTLDR: {tldr}\\n\\nAction Items:\\n{action_items}\\n\\nTopics: {main_topics}",
      ),

      // 6. Email full summary
      emailNode(
        "6",
        "Email full summary",
        1000,
        "{requester_email}",
        "Summary: {main_topics} ({word_count_original} words -> {word_count_summary})",
        "<h2>Content Summary</h2><p><strong>Source:</strong> {source_type} | <strong>Original:</strong> {word_count_original} words | <strong>Summary:</strong> {word_count_summary} words</p><h3>TLDR</h3><p>{tldr}</p><h3>Concise Summary</h3><p>{concise_summary}</p><h3>Detailed Summary</h3><p>{detailed_summary}</p><h3>Key Takeaways</h3><p>{key_takeaways}</p><h3>Action Items</h3><p>{action_items}</p><h3>Topics Covered</h3><p>{main_topics}</p>",
        460,
      ),

      // 7. Log summary metrics
      n("7", "hitApi", "Log to summary tracking sheet", 1240, {
        hitApiDetails: {
          method: "POST",
          url: "https://sheets.googleapis.com/v4/spreadsheets/{sheet_id}/values/Summaries!A1:append?valueInputOption=USER_ENTERED",
          headers: [
            { key: "Authorization", value: "Bearer {google_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "values",
              value:
                '[["{requester_name}","{source_type}","{word_count_original}","{word_count_summary}","{main_topics}"]]',
            },
          ],
          variables: [
            {
              name: "updates.updatedRows",
              customName: "rows_logged",
              type: "number",
            },
          ],
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"), // action items -> Slack + email
      e("4", "6", "output_2"), // no action items -> email only
      e("5", "6"),
      e("6", "7"),
    ],
    summary: `**AI Summarizer Bot** — Multi-level content summarization with action item extraction.\n\n1. Content submitted via Slack, form, or API\n2. **AI extracts** content, desired length, format, source type\n3. **AI generates** TLDR, concise summary, detailed summary, key takeaways, action items\n4. Action items found -> **Slack notification** with highlights\n5. Full summary **emailed** with all levels and metrics\n6. Summary metrics **logged to Google Sheets**\n\n**To activate:** Say "connect openai", "connect slack", "connect email", "connect google sheets"`,
  },

  // ===================================================================
  // 84. AI SUPPORT CHATBOT (9 nodes)
  // ===================================================================

  "AI Support Chatbot": {
    nodes: [
      trigger("Webhook: Customer sends support message", "webhook_app", {
        source: "Intercom / Zendesk Chat / Website Widget",
        event: "conversation.user.replied",
        howItStarts:
          "A customer sends a message through your support chat widget",
        setup:
          "In your chat platform (Intercom, Zendesk Chat, or custom widget), configure webhooks for new messages to your workflow URL.",
        inputFields: [],
      }),

      // 2. Extract message details
      aiNode(
        "2",
        "AI extract chat message details",
        280,
        "Read this chat webhook payload and extract: customer name, customer email, message text, conversation ID, previous messages if any, customer plan/tier, and timestamp. Respond JSON: {customer_name: string, customer_email: string, message_text: string, conversation_id: string, previous_context: string, customer_plan: string}",
        "{webhook_payload}",
        [
          { field: "customer_name", as: "customer_name", type: "string" },
          { field: "customer_email", as: "customer_email", type: "string" },
          { field: "message_text", as: "message_text", type: "string" },
          { field: "conversation_id", as: "conversation_id", type: "string" },
          { field: "previous_context", as: "previous_context", type: "string" },
          { field: "customer_plan", as: "customer_plan", type: "string" },
        ],
      ),

      // 3. Search knowledge base
      n("3", "hitApi", "Search knowledge base", 520, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.openai.com/v1/embeddings",
          headers: [
            { key: "Authorization", value: "Bearer {openai_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "model", value: "text-embedding-3-small" },
            { key: "input", value: "{message_text}" },
          ],
          variables: [
            {
              name: "data[0].embedding",
              customName: "query_embedding",
              type: "string",
            },
          ],
        },
      }),

      // 4. AI generate response with context
      aiNode(
        "4",
        "AI generate support response",
        760,
        "You are a friendly, knowledgeable support agent. Answer the customer question using the knowledge base context and conversation history. Be concise but thorough. If you cannot answer confidently, say so and offer to connect with a human agent. Always maintain a helpful tone. Respond JSON: {response_text: string, confidence: number, category: string, needs_human: string, suggested_article: string, follow_up_question: string}",
        "Customer: {customer_name} (Plan: {customer_plan})\\nMessage: {message_text}\\nPrevious context: {previous_context}\\nKnowledge base match: {query_embedding}",
        [
          { field: "response_text", as: "response_text", type: "string" },
          { field: "confidence", as: "ai_confidence", type: "number" },
          { field: "category", as: "ticket_category", type: "string" },
          { field: "needs_human", as: "needs_human", type: "string" },
          {
            field: "suggested_article",
            as: "suggested_article",
            type: "string",
          },
          {
            field: "follow_up_question",
            as: "follow_up_question",
            type: "string",
          },
        ],
      ),

      // 5. Check confidence threshold
      n("5", "condition", "AI confidence >= 80%?", 1000, {
        conditionData: [
          {
            variable: "ai_confidence",
            value: "80",
            comparison_operator: "GREATER THAN OR EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 6. Send AI response via chat API
      n("6", "hitApi", "Send AI response to customer", 1240, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.intercom.io/conversations/{conversation_id}/reply",
          headers: [
            { key: "Authorization", value: "Bearer {intercom_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "message_type", value: "comment" },
            { key: "type", value: "admin" },
            { key: "admin_id", value: "{bot_admin_id}" },
            { key: "body", value: "{response_text}" },
          ],
          variables: [
            {
              name: "conversation_id",
              customName: "reply_conversation_id",
              type: "string",
            },
          ],
        },
      }),

      // 7. Handoff to human agent (low confidence)
      n(
        "7",
        "approvalGate",
        "Human agent takes over",
        1240,
        {
          approvalGateData: {
            message:
              "AI confidence too low ({ai_confidence}%) for: {customer_name}\\nCategory: {ticket_category}\\nQuestion: {message_text}\\nDraft response: {response_text}\\n\\nTake over this conversation or approve AI draft.",
          },
        },
        460,
      ),

      // 8. Notify support team on Slack
      slackNode(
        "8",
        "Log to #support-ai channel",
        1480,
        "AI Support: {customer_name} ({customer_plan})\\nCategory: {ticket_category} | Confidence: {ai_confidence}%\\nQ: {message_text}\\nA: {response_text}\\nHuman needed: {needs_human}",
      ),

      // 9. Log conversation metrics
      n("9", "hitApi", "Log to analytics", 1720, {
        hitApiDetails: {
          method: "POST",
          url: "https://sheets.googleapis.com/v4/spreadsheets/{sheet_id}/values/ChatbotLog!A1:append?valueInputOption=USER_ENTERED",
          headers: [
            { key: "Authorization", value: "Bearer {google_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "values",
              value:
                '[["{customer_name}","{customer_plan}","{ticket_category}","{ai_confidence}","{needs_human}","{message_text}"]]',
            },
          ],
          variables: [
            {
              name: "updates.updatedRows",
              customName: "rows_logged",
              type: "number",
            },
          ],
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"), // high confidence -> auto-respond
      e("5", "7", "output_2"), // low confidence -> human handoff
      e("6", "8"),
      e("7", "6"), // approved by human -> send response
      e("8", "9"),
    ],
    summary: `**AI Support Chatbot** — Intelligent customer support with knowledge base search and human handoff.\n\n1. Customer message arrives via chat widget\n2. **AI extracts** customer details, message, plan, conversation history\n3. **Knowledge base** searched using embeddings for relevant articles\n4. **AI generates** contextual response with confidence score\n5. High confidence (>=80%) -> **auto-respond** via chat API\n6. Low confidence -> **human agent handoff** with draft response\n7. All interactions **logged to Slack** and **Google Sheets** for analytics\n\n**To activate:** Say "connect openai", "connect intercom", "connect slack", "connect google sheets"`,
  },

  // ===================================================================
  // 85. AI SUPPORT CONVERSATION SUMMARY (7 nodes)
  // ===================================================================

  "AI Support Conversation Summary": {
    nodes: [
      trigger("Webhook: Support conversation closed", "webhook_app", {
        source: "Zendesk / Intercom / Freshdesk",
        event: "conversation.closed",
        howItStarts:
          "A support conversation is marked as resolved/closed in your helpdesk",
        setup:
          "In your helpdesk, add a webhook trigger for conversation/ticket closed events. Point to your workflow URL.",
        inputFields: [],
      }),

      // 2. Fetch full conversation
      n("2", "hitApi", "Fetch full conversation transcript", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.intercom.io/conversations/{conversation_id}",
          headers: [
            { key: "Authorization", value: "Bearer {intercom_token}" },
            { key: "Accept", value: "application/json" },
          ],
          variables: [
            {
              name: "conversation_parts.conversation_parts",
              customName: "full_transcript",
              type: "string",
            },
            {
              name: "conversation_message.subject",
              customName: "ticket_subject",
              type: "string",
            },
            {
              name: "contacts.contacts[0].email",
              customName: "customer_email",
              type: "string",
            },
            {
              name: "contacts.contacts[0].name",
              customName: "customer_name",
              type: "string",
            },
            { name: "assignee.name", customName: "agent_name", type: "string" },
          ],
        },
      }),

      // 3. AI summarize conversation
      aiNode(
        "3",
        "AI summarize support conversation",
        520,
        "You are a support operations analyst. Summarize this support conversation into: a one-line summary, the customer issue, resolution provided, customer sentiment at close, time-to-resolution estimate, whether the issue is fully resolved or needs follow-up, and any product feedback. Respond JSON: {one_line_summary: string, customer_issue: string, resolution: string, close_sentiment: string, fully_resolved: string, needs_followup: string, product_feedback: string, tags: string}",
        "Subject: {ticket_subject}\\nCustomer: {customer_name}\\nAgent: {agent_name}\\nTranscript:\\n{full_transcript}",
        [
          { field: "one_line_summary", as: "one_line_summary", type: "string" },
          { field: "customer_issue", as: "customer_issue", type: "string" },
          { field: "resolution", as: "resolution", type: "string" },
          { field: "close_sentiment", as: "close_sentiment", type: "string" },
          { field: "fully_resolved", as: "fully_resolved", type: "string" },
          { field: "needs_followup", as: "needs_followup", type: "string" },
          { field: "product_feedback", as: "product_feedback", type: "string" },
          { field: "tags", as: "tags", type: "string" },
        ],
      ),

      // 4. Check if follow-up needed
      n("4", "condition", "Follow-up needed?", 760, {
        conditionData: [
          {
            variable: "needs_followup",
            value: "true",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 5. Create follow-up task in helpdesk
      n("5", "hitApi", "Create follow-up task", 1000, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.intercom.io/conversations/{conversation_id}/reply",
          headers: [
            { key: "Authorization", value: "Bearer {intercom_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "message_type", value: "note" },
            { key: "type", value: "admin" },
            { key: "admin_id", value: "{bot_admin_id}" },
            {
              key: "body",
              value:
                "FOLLOW-UP NEEDED: {needs_followup}\\nSummary: {one_line_summary}\\nResolution: {resolution}",
            },
          ],
          variables: [
            {
              name: "conversation_id",
              customName: "followup_conversation_id",
              type: "string",
            },
          ],
        },
      }),

      // 6. Update CRM with conversation summary
      n("6", "hitApi", "Update CRM with conversation summary", 1240, {
        hitApiDetails: {
          method: "PATCH",
          url: "https://api.hubapi.com/crm/v3/objects/contacts/{contact_id}",
          headers: [
            { key: "Authorization", value: "Bearer {hubspot_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "properties",
              value:
                '{"last_support_summary":"{one_line_summary}","last_support_sentiment":"{close_sentiment}","last_support_tags":"{tags}","last_support_resolved":"{fully_resolved}"}',
            },
          ],
          variables: [
            { name: "id", customName: "contact_updated_id", type: "string" },
          ],
        },
      }),

      // 7. Log to analytics sheet
      n("7", "hitApi", "Log conversation summary metrics", 1480, {
        hitApiDetails: {
          method: "POST",
          url: "https://sheets.googleapis.com/v4/spreadsheets/{sheet_id}/values/ConversationSummaries!A1:append?valueInputOption=USER_ENTERED",
          headers: [
            { key: "Authorization", value: "Bearer {google_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "values",
              value:
                '[["{customer_name}","{agent_name}","{one_line_summary}","{close_sentiment}","{fully_resolved}","{tags}","{product_feedback}"]]',
            },
          ],
          variables: [
            {
              name: "updates.updatedRows",
              customName: "rows_logged",
              type: "number",
            },
          ],
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"), // needs follow-up -> create task
      e("4", "6", "output_2"), // fully resolved -> CRM update
      e("5", "6"),
      e("6", "7"),
    ],
    summary: `**AI Support Conversation Summary** — Automatically summarizes closed support conversations for CRM and analytics.\n\n1. Conversation closed in helpdesk triggers workflow\n2. **Full transcript fetched** from Intercom/Zendesk\n3. **AI summarizes** issue, resolution, sentiment, tags, product feedback\n4. Follow-up needed -> **task created** in helpdesk\n5. **CRM updated** with conversation summary and sentiment\n6. All summaries **logged to Google Sheets** for team analytics\n\n**To activate:** Say "connect openai", "connect intercom", "connect hubspot", "connect google sheets"`,
  },

  // ===================================================================
  // 86. AI SWOT ANALYSIS BOT (8 nodes)
  // ===================================================================

  "AI SWOT Analysis Bot": {
    nodes: [
      trigger("Webhook: SWOT analysis requested", "webhook_form", {
        source: "Slack / Internal Tool / Form",
        event: "swot.requested",
        howItStarts:
          "A team member requests a SWOT analysis for a project, product, or business initiative",
        setup:
          "POST the analysis subject and context to your workflow URL via Slack slash command or form. Include the company/product name, industry, and any relevant data.",
        inputFields: [],
      }),

      // 2. Extract SWOT request
      aiNode(
        "2",
        "AI extract SWOT parameters",
        280,
        "Read this SWOT analysis request and extract: subject (company/product/initiative), industry, context/background, competitors, target market, and requester info. Respond JSON: {subject: string, industry: string, context: string, competitors: string, target_market: string, requester_name: string, requester_email: string}",
        "{webhook_payload}",
        [
          { field: "subject", as: "subject", type: "string" },
          { field: "industry", as: "industry", type: "string" },
          { field: "context", as: "context", type: "string" },
          { field: "competitors", as: "competitors", type: "string" },
          { field: "target_market", as: "target_market", type: "string" },
          { field: "requester_name", as: "requester_name", type: "string" },
          { field: "requester_email", as: "requester_email", type: "string" },
        ],
      ),

      // 3. Market research via Serper
      n("3", "hitApi", "Market research via Serper", 520, {
        hitApiDetails: {
          method: "POST",
          url: "https://google.serper.dev/search",
          headers: [
            { key: "X-API-KEY", value: "{serper_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "q",
              value:
                "{subject} {industry} market analysis strengths weaknesses opportunities threats {competitors}",
            },
            { key: "num", value: "10" },
          ],
          variables: [
            { name: "organic", customName: "market_data", type: "string" },
          ],
        },
      }),

      // 4. AI generate comprehensive SWOT
      aiNode(
        "4",
        "AI generate SWOT analysis",
        760,
        "You are a senior strategy consultant. Produce a comprehensive SWOT analysis. For each quadrant provide 4-5 specific, actionable points with evidence. Prioritize by impact. Include strategic recommendations that leverage strengths against opportunities and address weaknesses against threats. Respond JSON: {strengths: string, weaknesses: string, opportunities: string, threats: string, strategic_priorities: string, quick_wins: string, risk_mitigations: string, overall_assessment: string}",
        "Subject: {subject}\\nIndustry: {industry}\\nContext: {context}\\nCompetitors: {competitors}\\nTarget market: {target_market}\\nMarket data: {market_data}",
        [
          { field: "strengths", as: "strengths", type: "string" },
          { field: "weaknesses", as: "weaknesses", type: "string" },
          { field: "opportunities", as: "opportunities", type: "string" },
          { field: "threats", as: "threats", type: "string" },
          {
            field: "strategic_priorities",
            as: "strategic_priorities",
            type: "string",
          },
          { field: "quick_wins", as: "quick_wins", type: "string" },
          { field: "risk_mitigations", as: "risk_mitigations", type: "string" },
          {
            field: "overall_assessment",
            as: "overall_assessment",
            type: "string",
          },
        ],
      ),

      // 5. Approval gate for strategy review
      n("5", "approvalGate", "Strategy lead reviews SWOT", 1000, {
        approvalGateData: {
          message:
            "SWOT Analysis for: {subject}\\nIndustry: {industry}\\n\\nStrengths: {strengths}\\nWeaknesses: {weaknesses}\\nOpportunities: {opportunities}\\nThreats: {threats}\\n\\nStrategic Priorities: {strategic_priorities}\\n\\nApprove to distribute.",
        },
      }),

      // 6. Store in Notion
      n("6", "hitApi", "Store SWOT in Notion", 1240, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.notion.com/v1/pages",
          headers: [
            { key: "Authorization", value: "Bearer {notion_key}" },
            { key: "Notion-Version", value: "2022-06-28" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "parent", value: '{"database_id":"{notion_strategy_db}"}' },
            {
              key: "properties",
              value:
                '{"Title":{"title":[{"text":{"content":"SWOT: {subject}"}}]},"Industry":{"select":{"name":"{industry}"}},"Assessment":{"rich_text":[{"text":{"content":"{overall_assessment}"}}]}}',
            },
          ],
          variables: [
            { name: "id", customName: "notion_page_id", type: "string" },
          ],
        },
      }),

      // 7. Email SWOT report
      emailNode(
        "7",
        "Email SWOT report",
        1480,
        "{requester_email}",
        "SWOT Analysis: {subject}",
        "<h1>SWOT Analysis: {subject}</h1><p><em>Industry: {industry} | Overall: {overall_assessment}</em></p><table border='1' cellpadding='10'><tr><td><h3>Strengths</h3><p>{strengths}</p></td><td><h3>Weaknesses</h3><p>{weaknesses}</p></td></tr><tr><td><h3>Opportunities</h3><p>{opportunities}</p></td><td><h3>Threats</h3><p>{threats}</p></td></tr></table><h3>Strategic Priorities</h3><p>{strategic_priorities}</p><h3>Quick Wins</h3><p>{quick_wins}</p><h3>Risk Mitigations</h3><p>{risk_mitigations}</p>",
      ),

      // 8. Notify team via Slack
      slackNode(
        "8",
        "Notify team: SWOT complete",
        1720,
        "SWOT Analysis completed for *{subject}*\\nAssessment: {overall_assessment}\\nStrategic priorities: {strategic_priorities}\\nRequester: {requester_name}\\nNotion: {notion_page_id}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary: `**AI SWOT Analysis Bot** — Comprehensive SWOT analysis with market research and strategy recommendations.\n\n1. SWOT request submitted with subject, industry, competitors\n2. **Serper API** gathers market intelligence\n3. **AI generates** full SWOT: 4-5 points per quadrant, strategic priorities, quick wins, risk mitigations\n4. **Strategy lead approval** before distribution\n5. Analysis stored in **Notion** strategy database\n6. Report **emailed** to requester with formatted SWOT table\n7. Team **notified via Slack** with Notion link\n\n**To activate:** Say "connect openai", "connect serper", "connect notion", "connect email", "connect slack"`,
  },

  // ===================================================================
  // 87. AI TREND PREDICTION AGENT (9 nodes)
  // ===================================================================

  "AI Trend Prediction Agent": {
    nodes: [
      trigger("Schedule: Weekly trend analysis", "schedule", {
        schedule: "Every Monday at 8:00 AM",
        howItStarts:
          "Runs weekly to analyze market trends and predict upcoming shifts in your industry",
        setup:
          "Activate the workflow and configure your industry keywords. Connect Serper for web data gathering.",
        inputFields: [
          {
            name: "industry",
            type: "string",
            example: "SaaS marketing automation",
          },
          {
            name: "tracking_keywords",
            type: "string",
            example: "AI marketing, workflow automation, no-code",
          },
        ],
      }),

      // 2. Gather trend data from web
      n("2", "hitApi", "Serper: trending topics search", 280, {
        hitApiDetails: {
          method: "POST",
          url: "https://google.serper.dev/search",
          headers: [
            { key: "X-API-KEY", value: "{serper_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "q", value: "{industry} trends 2026 {tracking_keywords}" },
            { key: "num", value: "15" },
            { key: "tbs", value: "qdr:w" },
          ],
          variables: [
            { name: "organic", customName: "trend_results", type: "string" },
            { name: "news", customName: "news_results", type: "string" },
          ],
        },
      }),

      // 3. Gather Google Trends data
      n("3", "hitApi", "Serper: related searches + people also ask", 520, {
        hitApiDetails: {
          method: "POST",
          url: "https://google.serper.dev/search",
          headers: [
            { key: "X-API-KEY", value: "{serper_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "q",
              value: "{tracking_keywords} emerging technology market growth",
            },
            { key: "num", value: "10" },
          ],
          variables: [
            {
              name: "relatedSearches",
              customName: "related_searches",
              type: "string",
            },
            {
              name: "peopleAlsoAsk",
              customName: "people_also_ask",
              type: "string",
            },
          ],
        },
      }),

      // 4. AI analyze trends and predict
      aiNode(
        "4",
        "AI trend analysis and predictions",
        760,
        "You are a market intelligence analyst specializing in trend prediction. Analyze the data and identify: current hot trends (already mainstream), emerging trends (early adoption), declining trends, and predict 3 upcoming trends for the next 6 months with confidence levels. Provide actionable recommendations. Respond JSON: {hot_trends: string, emerging_trends: string, declining_trends: string, prediction_1: string, prediction_1_confidence: number, prediction_2: string, prediction_2_confidence: number, prediction_3: string, prediction_3_confidence: number, recommendations: string, market_sentiment: string}",
        "Industry: {industry}\\nKeywords: {tracking_keywords}\\nWeb results: {trend_results}\\nNews: {news_results}\\nRelated searches: {related_searches}\\nPeople also ask: {people_also_ask}",
        [
          { field: "hot_trends", as: "hot_trends", type: "string" },
          { field: "emerging_trends", as: "emerging_trends", type: "string" },
          { field: "declining_trends", as: "declining_trends", type: "string" },
          { field: "prediction_1", as: "prediction_1", type: "string" },
          {
            field: "prediction_1_confidence",
            as: "prediction_1_confidence",
            type: "number",
          },
          { field: "prediction_2", as: "prediction_2", type: "string" },
          {
            field: "prediction_2_confidence",
            as: "prediction_2_confidence",
            type: "number",
          },
          { field: "prediction_3", as: "prediction_3", type: "string" },
          {
            field: "prediction_3_confidence",
            as: "prediction_3_confidence",
            type: "number",
          },
          { field: "recommendations", as: "recommendations", type: "string" },
          { field: "market_sentiment", as: "market_sentiment", type: "string" },
        ],
      ),

      // 5. Check for high-confidence disruptive prediction
      n("5", "condition", "Disruptive prediction (confidence > 80)?", 1000, {
        conditionData: [
          {
            variable: "prediction_1_confidence",
            value: "80",
            comparison_operator: "GREATER THAN",
            logical_operator: "AND",
          },
        ],
      }),

      // 6. Alert leadership on Slack (disruptive trend)
      slackNode(
        "6",
        "Alert #strategy: disruptive trend predicted",
        1240,
        "HIGH-CONFIDENCE TREND PREDICTION\\nIndustry: {industry}\\n\\nPrediction: {prediction_1} (Confidence: {prediction_1_confidence}%)\\n\\nMarket sentiment: {market_sentiment}\\nRecommendations: {recommendations}",
      ),

      // 7. Store trend report in Notion
      n(
        "7",
        "hitApi",
        "Store trend report in Notion",
        1240,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://api.notion.com/v1/pages",
            headers: [
              { key: "Authorization", value: "Bearer {notion_key}" },
              { key: "Notion-Version", value: "2022-06-28" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              { key: "parent", value: '{"database_id":"{notion_trends_db}"}' },
              {
                key: "properties",
                value:
                  '{"Title":{"title":[{"text":{"content":"Weekly Trend Report: {industry}"}}]},"Sentiment":{"select":{"name":"{market_sentiment}"}},"Top Prediction":{"rich_text":[{"text":{"content":"{prediction_1}"}}]}}',
              },
            ],
            variables: [
              { name: "id", customName: "notion_page_id", type: "string" },
            ],
          },
        },
        460,
      ),

      // 8. Email weekly trend digest
      emailNode(
        "8",
        "Email weekly trend digest",
        1480,
        "{strategy_team_email}",
        "Weekly Trend Report: {industry} - {market_sentiment}",
        "<h1>Weekly Trend Report: {industry}</h1><p><strong>Market Sentiment:</strong> {market_sentiment}</p><h2>Hot Trends (Mainstream)</h2><p>{hot_trends}</p><h2>Emerging Trends (Early Adoption)</h2><p>{emerging_trends}</p><h2>Declining Trends</h2><p>{declining_trends}</p><h2>Predictions (Next 6 Months)</h2><ol><li>{prediction_1} (Confidence: {prediction_1_confidence}%)</li><li>{prediction_2} (Confidence: {prediction_2_confidence}%)</li><li>{prediction_3} (Confidence: {prediction_3_confidence}%)</li></ol><h2>Recommendations</h2><p>{recommendations}</p>",
      ),

      // 9. Log metrics for trend tracking over time
      n("9", "hitApi", "Log trend metrics to tracking sheet", 1720, {
        hitApiDetails: {
          method: "POST",
          url: "https://sheets.googleapis.com/v4/spreadsheets/{sheet_id}/values/TrendMetrics!A1:append?valueInputOption=USER_ENTERED",
          headers: [
            { key: "Authorization", value: "Bearer {google_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "values",
              value:
                '[["{industry}","{market_sentiment}","{hot_trends}","{prediction_1}","{prediction_1_confidence}","{prediction_2}","{prediction_2_confidence}"]]',
            },
          ],
          variables: [
            {
              name: "updates.updatedRows",
              customName: "rows_logged",
              type: "number",
            },
          ],
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"), // disruptive -> alert leadership
      e("5", "7", "output_2"), // normal -> store in Notion
      e("6", "7"),
      e("7", "8"),
      e("8", "9"),
    ],
    summary: `**AI Trend Prediction Agent** — Weekly market intelligence with trend prediction and strategic recommendations.\n\n1. Runs **every Monday** for the specified industry\n2. **Serper API** gathers web results, news, related searches, and people-also-ask data\n3. **AI analyzes** hot, emerging, and declining trends\n4. **3 predictions** generated with confidence levels for next 6 months\n5. High-confidence disruptive predictions trigger **leadership Slack alert**\n6. Full report stored in **Notion** trends database\n7. Weekly digest **emailed** to strategy team\n8. Metrics **logged to Google Sheets** for historical tracking\n\n**To activate:** Say "connect openai", "connect serper", "connect notion", "connect email", "connect slack", "connect google sheets"`,
  },

  // ===================================================================
  // 88. AI WORKFLOW DEBUGGER AGENT (8 nodes)
  // ===================================================================

  "AI Workflow Debugger Agent": {
    nodes: [
      trigger("Webhook: Workflow error reported", "webhook_app", {
        source: "Monitoring / Error Tracking",
        event: "workflow.error",
        howItStarts:
          "A workflow execution fails or produces unexpected results, triggering an error webhook",
        setup:
          "Configure your workflow engine to POST error details to this workflow URL when executions fail. Include the workflow ID, error message, and execution log.",
        inputFields: [],
      }),

      // 2. Extract error details
      aiNode(
        "2",
        "AI extract error details",
        280,
        "Read this workflow error payload and extract: workflow name/ID, failed node ID, error message, error type, execution log, input data that caused the failure, and timestamp. Respond JSON: {workflow_name: string, failed_node_id: string, error_message: string, error_type: string, execution_log: string, input_data: string, timestamp: string}",
        "{webhook_payload}",
        [
          { field: "workflow_name", as: "workflow_name", type: "string" },
          { field: "failed_node_id", as: "failed_node_id", type: "string" },
          { field: "error_message", as: "error_message", type: "string" },
          { field: "error_type", as: "error_type", type: "string" },
          { field: "execution_log", as: "execution_log", type: "string" },
          { field: "input_data", as: "input_data", type: "string" },
          { field: "timestamp", as: "error_timestamp", type: "string" },
        ],
      ),

      // 3. AI diagnose root cause
      aiNode(
        "3",
        "AI diagnose root cause",
        520,
        "You are a workflow debugging expert. Analyze this workflow error and diagnose: the root cause, whether it is a data issue (bad input), configuration issue (wrong API key, URL), logic issue (wrong condition), or infrastructure issue (timeout, rate limit). Provide a specific fix recommendation and prevention strategy. Respond JSON: {root_cause: string, error_category: string, fix_recommendation: string, prevention_strategy: string, severity: string, is_recurring: string, confidence: number}",
        "Workflow: {workflow_name}\\nFailed node: {failed_node_id}\\nError: {error_message}\\nType: {error_type}\\nLog: {execution_log}\\nInput: {input_data}",
        [
          { field: "root_cause", as: "root_cause", type: "string" },
          { field: "error_category", as: "error_category", type: "string" },
          {
            field: "fix_recommendation",
            as: "fix_recommendation",
            type: "string",
          },
          {
            field: "prevention_strategy",
            as: "prevention_strategy",
            type: "string",
          },
          { field: "severity", as: "severity", type: "string" },
          { field: "is_recurring", as: "is_recurring", type: "string" },
          { field: "confidence", as: "diagnosis_confidence", type: "number" },
        ],
      ),

      // 4. Check severity
      n("4", "condition", "Is severity critical?", 760, {
        conditionData: [
          {
            variable: "severity",
            value: "critical",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 5. Alert team on Slack (critical)
      slackNode(
        "5",
        "Alert #ops: critical workflow failure",
        1000,
        "CRITICAL WORKFLOW FAILURE\\nWorkflow: {workflow_name}\\nNode: {failed_node_id}\\nError: {error_message}\\n\\nRoot cause: {root_cause}\\nCategory: {error_category}\\nFix: {fix_recommendation}\\nConfidence: {diagnosis_confidence}%",
      ),

      // 6. Create incident ticket in Jira
      n("6", "hitApi", "Create Jira incident ticket", 1240, {
        hitApiDetails: {
          method: "POST",
          url: "https://{jira_domain}.atlassian.net/rest/api/3/issue",
          headers: [
            { key: "Authorization", value: "Basic {jira_auth}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "fields",
              value:
                '{"project":{"key":"{jira_project_key}"},"summary":"Workflow Error: {workflow_name} - {error_category}","description":{"type":"doc","version":1,"content":[{"type":"paragraph","content":[{"type":"text","text":"Root Cause: {root_cause}\\nFix: {fix_recommendation}\\nPrevention: {prevention_strategy}\\nError: {error_message}"}]}]},"issuetype":{"name":"Bug"},"priority":{"name":"High"}}',
            },
          ],
          variables: [
            { name: "key", customName: "jira_ticket_key", type: "string" },
          ],
        },
      }),

      // 7. Email debug report
      emailNode(
        "7",
        "Email debug report to workflow owner",
        1000,
        "{workflow_owner_email}",
        "Workflow Debug: {workflow_name} - {error_category}",
        "<h2>Workflow Debug Report</h2><p><strong>Workflow:</strong> {workflow_name}</p><p><strong>Failed Node:</strong> {failed_node_id}</p><p><strong>Severity:</strong> {severity}</p><h3>Error</h3><p>{error_message}</p><h3>Root Cause Analysis</h3><p><strong>Category:</strong> {error_category}</p><p>{root_cause}</p><h3>Fix Recommendation</h3><p>{fix_recommendation}</p><h3>Prevention Strategy</h3><p>{prevention_strategy}</p><p><strong>Diagnosis Confidence:</strong> {diagnosis_confidence}%</p>",
        460,
      ),

      // 8. Log error for analytics
      n("8", "hitApi", "Log error to analytics sheet", 1480, {
        hitApiDetails: {
          method: "POST",
          url: "https://sheets.googleapis.com/v4/spreadsheets/{sheet_id}/values/WorkflowErrors!A1:append?valueInputOption=USER_ENTERED",
          headers: [
            { key: "Authorization", value: "Bearer {google_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "values",
              value:
                '[["{workflow_name}","{failed_node_id}","{error_category}","{severity}","{root_cause}","{diagnosis_confidence}","{error_timestamp}"]]',
            },
          ],
          variables: [
            {
              name: "updates.updatedRows",
              customName: "rows_logged",
              type: "number",
            },
          ],
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"), // critical -> Slack alert + Jira
      e("4", "7", "output_2"), // non-critical -> email report
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary: `**AI Workflow Debugger Agent** — Automated workflow error diagnosis with root cause analysis and incident management.\n\n1. Workflow error webhook triggers debugging pipeline\n2. **AI extracts** error details: workflow, node, message, log, input data\n3. **AI diagnoses** root cause: data, config, logic, or infrastructure issue\n4. Critical errors trigger **Slack alert** and **Jira incident ticket**\n5. Non-critical errors get **email debug report** to workflow owner\n6. All errors **logged to Google Sheets** for trend analysis\n\n**To activate:** Say "connect openai", "connect slack", "connect jira", "connect email", "connect google sheets"`,
  },

  // ===================================================================
  // 89. AI WRITING ASSISTANT (7 nodes)
  // ===================================================================

  "AI Writing Assistant": {
    nodes: [
      trigger("Webhook: Writing request submitted", "webhook_form", {
        source: "Slack / Form / Internal Tool",
        event: "writing.requested",
        howItStarts:
          "A team member submits a writing request with topic, tone, and audience specifications",
        setup:
          "Create a Slack slash command or form that POSTs to your workflow URL. Include topic, content type, tone, audience, and word count.",
        inputFields: [],
      }),

      // 2. Extract writing brief
      aiNode(
        "2",
        "AI extract writing brief",
        280,
        "Read this writing request and extract: topic, content type (blog/email/ad/social/whitepaper), desired tone (formal/casual/persuasive/informative), target audience, word count, key points to cover, and requester info. Respond JSON: {topic: string, content_type: string, tone: string, target_audience: string, word_count: number, key_points: string, requester_name: string, requester_email: string}",
        "{webhook_payload}",
        [
          { field: "topic", as: "topic", type: "string" },
          { field: "content_type", as: "content_type", type: "string" },
          { field: "tone", as: "tone", type: "string" },
          { field: "target_audience", as: "target_audience", type: "string" },
          { field: "word_count", as: "target_word_count", type: "number" },
          { field: "key_points", as: "key_points", type: "string" },
          { field: "requester_name", as: "requester_name", type: "string" },
          { field: "requester_email", as: "requester_email", type: "string" },
        ],
      ),

      // 3. AI generate content
      aiNode(
        "3",
        "AI generate content draft",
        520,
        "You are an expert content writer. Write the requested content matching the exact tone, audience, and style specified. Include a compelling headline, engaging opening, well-structured body, and strong conclusion/CTA. Optimize for readability. Respond JSON: {headline: string, content_body: string, meta_description: string, cta: string, estimated_reading_time: string, seo_keywords: string}",
        "Topic: {topic}\\nType: {content_type}\\nTone: {tone}\\nAudience: {target_audience}\\nWord count: {target_word_count}\\nKey points: {key_points}",
        [
          { field: "headline", as: "headline", type: "string" },
          { field: "content_body", as: "content_body", type: "string" },
          { field: "meta_description", as: "meta_description", type: "string" },
          { field: "cta", as: "cta", type: "string" },
          {
            field: "estimated_reading_time",
            as: "reading_time",
            type: "string",
          },
          { field: "seo_keywords", as: "seo_keywords", type: "string" },
        ],
      ),

      // 4. Approval gate for editor review
      n("4", "approvalGate", "Editor reviews draft", 760, {
        approvalGateData: {
          message:
            "Writing draft for {requester_name}\\nType: {content_type} | Tone: {tone} | Audience: {target_audience}\\n\\nHeadline: {headline}\\n\\nContent preview: {meta_description}\\nReading time: {reading_time}\\n\\nApprove to deliver to requester.",
        },
      }),

      // 5. Email content to requester
      emailNode(
        "5",
        "Deliver content to requester",
        1000,
        "{requester_email}",
        "Your {content_type} is ready: {headline}",
        "<h2>{headline}</h2><p><em>Type: {content_type} | Reading time: {reading_time} | SEO Keywords: {seo_keywords}</em></p><hr>{content_body}<hr><p><strong>Call to Action:</strong> {cta}</p><p><strong>Meta Description:</strong> {meta_description}</p>",
      ),

      // 6. Store in Notion content library
      n("6", "hitApi", "Store in Notion content library", 1240, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.notion.com/v1/pages",
          headers: [
            { key: "Authorization", value: "Bearer {notion_key}" },
            { key: "Notion-Version", value: "2022-06-28" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "parent", value: '{"database_id":"{notion_content_db}"}' },
            {
              key: "properties",
              value:
                '{"Title":{"title":[{"text":{"content":"{headline}"}}]},"Type":{"select":{"name":"{content_type}"}},"Tone":{"select":{"name":"{tone}"}},"Author":{"rich_text":[{"text":{"content":"{requester_name}"}}]}}',
            },
          ],
          variables: [
            { name: "id", customName: "notion_page_id", type: "string" },
          ],
        },
      }),

      // 7. Log metrics
      slackNode(
        "7",
        "Log to #content-pipeline",
        1480,
        "Content delivered to {requester_name}\\nType: {content_type} | Topic: {topic}\\nHeadline: {headline}\\nReading time: {reading_time}\\nNotion: {notion_page_id}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
    summary: `**AI Writing Assistant** — Content generation pipeline with editor approval and content library management.\n\n1. Writing request submitted with topic, type, tone, audience\n2. **AI extracts** full writing brief from request\n3. **AI generates** headline, body, meta description, CTA, SEO keywords\n4. **Editor approval gate** before delivery\n5. Content **emailed** to requester\n6. Draft stored in **Notion content library**\n7. Delivery **logged to Slack** #content-pipeline\n\n**To activate:** Say "connect openai", "connect email", "connect notion", "connect slack"`,
  },

  // ===================================================================
  // 90. AI WRITING STYLE CLONER (8 nodes)
  // ===================================================================

  "AI Writing Style Cloner": {
    nodes: [
      trigger("Webhook: Style cloning request", "webhook_form", {
        source: "Form / Internal Tool",
        event: "style.clone.requested",
        howItStarts:
          "A user submits sample text to clone its writing style, along with new content to write in that style",
        setup:
          "POST to your workflow URL with: sample_text (the style to clone), new_topic (what to write about), and content_type.",
        inputFields: [],
      }),

      // 2. Extract cloning request
      aiNode(
        "2",
        "AI extract style cloning request",
        280,
        "Read this request and extract: the sample text whose style should be cloned, the new topic to write about, content type, word count target, and requester info. Respond JSON: {sample_text: string, new_topic: string, content_type: string, word_count: number, requester_name: string, requester_email: string}",
        "{webhook_payload}",
        [
          { field: "sample_text", as: "sample_text", type: "string" },
          { field: "new_topic", as: "new_topic", type: "string" },
          { field: "content_type", as: "content_type", type: "string" },
          { field: "word_count", as: "target_word_count", type: "number" },
          { field: "requester_name", as: "requester_name", type: "string" },
          { field: "requester_email", as: "requester_email", type: "string" },
        ],
      ),

      // 3. AI analyze writing style
      aiNode(
        "3",
        "AI analyze writing style DNA",
        520,
        "You are a linguistic analyst. Analyze this writing sample and extract its style DNA: sentence structure patterns, vocabulary level, tone markers, rhythm/cadence, use of metaphors/analogies, paragraph length patterns, voice (active/passive ratio), formality level, and unique quirks. Respond JSON: {sentence_structure: string, vocabulary_level: string, tone_markers: string, rhythm_pattern: string, metaphor_usage: string, paragraph_style: string, voice: string, formality: string, unique_quirks: string, style_summary: string}",
        "Sample text to analyze:\\n{sample_text}",
        [
          {
            field: "sentence_structure",
            as: "sentence_structure",
            type: "string",
          },
          { field: "vocabulary_level", as: "vocabulary_level", type: "string" },
          { field: "tone_markers", as: "tone_markers", type: "string" },
          { field: "rhythm_pattern", as: "rhythm_pattern", type: "string" },
          { field: "voice", as: "voice", type: "string" },
          { field: "formality", as: "formality", type: "string" },
          { field: "unique_quirks", as: "unique_quirks", type: "string" },
          { field: "style_summary", as: "style_summary", type: "string" },
        ],
      ),

      // 4. AI write new content in cloned style
      aiNode(
        "4",
        "AI write content in cloned style",
        760,
        "You are a style mimicry expert. Write new content on the given topic exactly matching this writing style DNA. Match the sentence structure, vocabulary, tone, rhythm, voice, formality, and quirks precisely. The reader should not be able to distinguish between the original author and your output. Respond JSON: {cloned_content: string, style_match_score: number, style_notes: string}",
        "STYLE DNA:\\nSentence structure: {sentence_structure}\\nVocabulary: {vocabulary_level}\\nTone: {tone_markers}\\nRhythm: {rhythm_pattern}\\nVoice: {voice}\\nFormality: {formality}\\nQuirks: {unique_quirks}\\n\\nNEW TOPIC: {new_topic}\\nContent type: {content_type}\\nWord count: {target_word_count}",
        [
          { field: "cloned_content", as: "cloned_content", type: "string" },
          {
            field: "style_match_score",
            as: "style_match_score",
            type: "number",
          },
          { field: "style_notes", as: "style_notes", type: "string" },
        ],
      ),

      // 5. Check style match quality
      n("5", "condition", "Style match score >= 80?", 1000, {
        conditionData: [
          {
            variable: "style_match_score",
            value: "80",
            comparison_operator: "GREATER THAN OR EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 6. Approval gate for low match scores
      n(
        "6",
        "approvalGate",
        "Editor reviews style match",
        1240,
        {
          approvalGateData: {
            message:
              "Style cloning result (score: {style_match_score}/100):\\n\\nStyle summary: {style_summary}\\nStyle notes: {style_notes}\\n\\nContent preview: {cloned_content}\\n\\nApprove to deliver, or reject for regeneration.",
          },
        },
        460,
      ),

      // 7. Email cloned content
      emailNode(
        "7",
        "Deliver style-cloned content",
        1240,
        "{requester_email}",
        "Style-Cloned Content: {new_topic} (Match: {style_match_score}%)",
        "<h2>Style-Cloned Content</h2><p><strong>Topic:</strong> {new_topic}</p><p><strong>Style Match:</strong> {style_match_score}%</p><p><strong>Style Summary:</strong> {style_summary}</p><hr><h3>Generated Content</h3>{cloned_content}<hr><p><em>Style Notes: {style_notes}</em></p>",
      ),

      // 8. Log to tracking sheet
      n("8", "hitApi", "Log style clone metrics", 1480, {
        hitApiDetails: {
          method: "POST",
          url: "https://sheets.googleapis.com/v4/spreadsheets/{sheet_id}/values/StyleClones!A1:append?valueInputOption=USER_ENTERED",
          headers: [
            { key: "Authorization", value: "Bearer {google_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "values",
              value:
                '[["{requester_name}","{new_topic}","{content_type}","{style_match_score}","{formality}","{voice}"]]',
            },
          ],
          variables: [
            {
              name: "updates.updatedRows",
              customName: "rows_logged",
              type: "number",
            },
          ],
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "7", "output_1"), // high match -> deliver directly
      e("5", "6", "output_2"), // low match -> editor review
      e("6", "7"),
      e("7", "8"),
    ],
    summary: `**AI Writing Style Cloner** — Analyze a writing sample and generate new content in the exact same style.\n\n1. User submits sample text + new topic to write\n2. **AI analyzes** style DNA: sentence structure, vocabulary, tone, rhythm, voice, quirks\n3. **AI generates** new content matching the cloned style precisely\n4. Style match score >= 80% -> **delivered directly**\n5. Low match scores routed to **editor review**\n6. Content **emailed** to requester with style analysis\n7. Metrics **logged to Google Sheets**\n\n**To activate:** Say "connect openai", "connect email", "connect google sheets"`,
  },

  // ===================================================================
  // 91. AFTER HOURS AI SUPPORT BOT (8 nodes)
  // ===================================================================

  "After Hours AI Support Bot": {
    nodes: [
      trigger("Webhook: Support message received", "webhook_app", {
        source: "Intercom / Zendesk / Chat Widget",
        event: "conversation.user.replied",
        howItStarts:
          "A customer sends a support message outside of business hours",
        setup:
          "Configure your chat platform to POST new messages to this workflow URL. The workflow checks business hours and routes accordingly.",
        inputFields: [],
      }),

      // 2. Extract message and check time
      aiNode(
        "2",
        "AI extract message + detect after-hours",
        280,
        "Read this support message payload and extract: customer name, email, message text, conversation ID, timestamp, and timezone. Determine if it is currently outside business hours (Mon-Fri 9am-6pm in the customer timezone). Respond JSON: {customer_name: string, customer_email: string, message_text: string, conversation_id: string, is_after_hours: string, current_time: string, timezone: string}",
        "{webhook_payload}",
        [
          { field: "customer_name", as: "customer_name", type: "string" },
          { field: "customer_email", as: "customer_email", type: "string" },
          { field: "message_text", as: "message_text", type: "string" },
          { field: "conversation_id", as: "conversation_id", type: "string" },
          { field: "is_after_hours", as: "is_after_hours", type: "string" },
          { field: "current_time", as: "current_time", type: "string" },
          { field: "timezone", as: "timezone", type: "string" },
        ],
      ),

      // 3. Check if after hours
      n("3", "condition", "Is it after hours?", 520, {
        conditionData: [
          {
            variable: "is_after_hours",
            value: "true",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 4. AI generate after-hours support response
      aiNode(
        "4",
        "AI generate after-hours response",
        760,
        "You are a helpful after-hours support bot. Answer the customer question as best you can. Be warm and acknowledge it is after hours. If you cannot resolve the issue, let them know a human agent will follow up during business hours. Respond JSON: {response_text: string, can_resolve: string, urgency: string, category: string, needs_morning_followup: string}",
        "Customer: {customer_name}\\nMessage: {message_text}\\nTime: {current_time} ({timezone})",
        [
          { field: "response_text", as: "response_text", type: "string" },
          { field: "can_resolve", as: "can_resolve", type: "string" },
          { field: "urgency", as: "urgency", type: "string" },
          { field: "category", as: "category", type: "string" },
          {
            field: "needs_morning_followup",
            as: "needs_morning_followup",
            type: "string",
          },
        ],
      ),

      // 5. Send AI response to customer
      n("5", "hitApi", "Send after-hours response", 1000, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.intercom.io/conversations/{conversation_id}/reply",
          headers: [
            { key: "Authorization", value: "Bearer {intercom_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "message_type", value: "comment" },
            { key: "type", value: "admin" },
            { key: "admin_id", value: "{bot_admin_id}" },
            { key: "body", value: "{response_text}" },
          ],
          variables: [
            {
              name: "conversation_id",
              customName: "reply_conversation_id",
              type: "string",
            },
          ],
        },
      }),

      // 6. Check if urgent and needs immediate human attention
      n("6", "condition", "Urgent issue needing immediate attention?", 1240, {
        conditionData: [
          {
            variable: "urgency",
            value: "critical",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 7. Page on-call via Slack
      slackNode(
        "7",
        "Page on-call: urgent after-hours issue",
        1480,
        "URGENT AFTER-HOURS SUPPORT\\nCustomer: {customer_name} ({customer_email})\\nTime: {current_time} ({timezone})\\nMessage: {message_text}\\nCategory: {category}\\nAI response sent: {can_resolve}",
      ),

      // 8. Queue for morning follow-up
      n(
        "8",
        "hitApi",
        "Queue for morning follow-up",
        1480,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://api.intercom.io/conversations/{conversation_id}/reply",
            headers: [
              { key: "Authorization", value: "Bearer {intercom_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              { key: "message_type", value: "note" },
              { key: "type", value: "admin" },
              { key: "admin_id", value: "{bot_admin_id}" },
              {
                key: "body",
                value:
                  "MORNING FOLLOW-UP NEEDED: {customer_name} contacted after hours at {current_time}. Category: {category}. AI resolved: {can_resolve}. Original message: {message_text}",
              },
            ],
            variables: [
              {
                name: "conversation_id",
                customName: "queued_conversation_id",
                type: "string",
              },
            ],
          },
        },
        460,
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4", "output_1"), // after hours -> AI respond
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_1"), // urgent -> page on-call
      e("6", "8", "output_2"), // not urgent -> queue for morning
    ],
    summary: `**After Hours AI Support Bot** — Intelligent after-hours support with urgency detection and on-call escalation.\n\n1. Customer message triggers workflow\n2. **AI detects** if message is outside business hours (Mon-Fri 9am-6pm)\n3. After-hours messages routed to **AI response generation**\n4. Response **sent via Intercom** acknowledging after-hours status\n5. Critical/urgent issues -> **on-call team paged via Slack**\n6. Non-urgent issues -> **queued as internal note** for morning follow-up\n7. Business-hours messages passed through normally\n\n**To activate:** Say "connect openai", "connect intercom", "connect slack"`,
  },

  // ===================================================================
  // 92. AGENTIC WORKFLOW BUILDER (9 nodes)
  // ===================================================================

  "Agentic Workflow Builder": {
    nodes: [
      trigger("Webhook: Workflow build request", "webhook_form", {
        source: "Slack / Internal Tool / Chat",
        event: "workflow.build.requested",
        howItStarts:
          "A user describes a workflow they want to build in natural language",
        setup:
          "POST a natural language workflow description to your workflow URL. The AI will design, validate, and prepare the workflow configuration.",
        inputFields: [],
      }),

      // 2. Extract workflow requirements
      aiNode(
        "2",
        "AI extract workflow requirements",
        280,
        "Read this workflow request and extract: the workflow goal, trigger type needed, steps described, integrations mentioned, conditions/branching needed, and any approval requirements. Respond JSON: {workflow_goal: string, trigger_type: string, steps_described: string, integrations: string, conditions: string, approvals_needed: string, requester_name: string, requester_email: string, complexity: string}",
        "{webhook_payload}",
        [
          { field: "workflow_goal", as: "workflow_goal", type: "string" },
          { field: "trigger_type", as: "trigger_type", type: "string" },
          { field: "steps_described", as: "steps_described", type: "string" },
          { field: "integrations", as: "integrations", type: "string" },
          { field: "conditions", as: "conditions", type: "string" },
          { field: "approvals_needed", as: "approvals_needed", type: "string" },
          { field: "requester_name", as: "requester_name", type: "string" },
          { field: "requester_email", as: "requester_email", type: "string" },
          { field: "complexity", as: "complexity", type: "string" },
        ],
      ),

      // 3. AI design workflow architecture
      aiNode(
        "3",
        "AI design workflow architecture",
        520,
        "You are a workflow architect. Design the workflow with: node types for each step (campaign, hitApi, sendEmail, sendSMS, condition, delayAction, runCode, approvalGate, whatsApp, pushNotification), the data flow between nodes, condition logic, error handling strategy, and estimated API integrations needed. Output a structured blueprint. Respond JSON: {node_count: number, node_list: string, edge_list: string, data_flow: string, condition_logic: string, error_handling: string, api_integrations: string, estimated_build_time: string}",
        "Goal: {workflow_goal}\\nTrigger: {trigger_type}\\nSteps: {steps_described}\\nIntegrations: {integrations}\\nConditions: {conditions}\\nApprovals: {approvals_needed}\\nComplexity: {complexity}",
        [
          { field: "node_count", as: "node_count", type: "number" },
          { field: "node_list", as: "node_list", type: "string" },
          { field: "edge_list", as: "edge_list", type: "string" },
          { field: "data_flow", as: "data_flow", type: "string" },
          { field: "condition_logic", as: "condition_logic", type: "string" },
          { field: "error_handling", as: "error_handling", type: "string" },
          { field: "api_integrations", as: "api_integrations", type: "string" },
          {
            field: "estimated_build_time",
            as: "estimated_build_time",
            type: "string",
          },
        ],
      ),

      // 4. AI validate workflow design
      aiNode(
        "4",
        "AI validate workflow logic",
        760,
        "You are a QA engineer for workflows. Validate this workflow design for: data dependency issues (does every node have its required inputs?), infinite loop risks, missing error handlers, unreachable nodes, and condition coverage gaps. Respond JSON: {is_valid: string, issues_found: string, issue_count: number, suggestions: string, risk_level: string}",
        "Nodes: {node_list}\\nEdges: {edge_list}\\nData flow: {data_flow}\\nConditions: {condition_logic}\\nError handling: {error_handling}",
        [
          { field: "is_valid", as: "is_valid", type: "string" },
          { field: "issues_found", as: "issues_found", type: "string" },
          { field: "issue_count", as: "issue_count", type: "number" },
          { field: "suggestions", as: "suggestions", type: "string" },
          { field: "risk_level", as: "risk_level", type: "string" },
        ],
      ),

      // 5. Check if design is valid
      n("5", "condition", "Design validation passed?", 1000, {
        conditionData: [
          {
            variable: "is_valid",
            value: "true",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 6. Approval gate for workflow deployment
      n("6", "approvalGate", "Admin approves workflow design", 1240, {
        approvalGateData: {
          message:
            "Workflow design for: {workflow_goal}\\nNodes: {node_count} | Build time: {estimated_build_time}\\nIntegrations: {api_integrations}\\n\\nNode list: {node_list}\\nData flow: {data_flow}\\nRisk level: {risk_level}\\n\\nApprove to generate workflow configuration.",
        },
      }),

      // 7. Store workflow config in database
      n("7", "hitApi", "Store workflow config via API", 1480, {
        hitApiDetails: {
          method: "POST",
          url: "https://{app_domain}/api/v1/workflows",
          headers: [
            { key: "Authorization", value: "Bearer {api_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "name", value: "{workflow_goal}" },
            { key: "nodes", value: "{node_list}" },
            { key: "edges", value: "{edge_list}" },
            { key: "created_by", value: "{requester_name}" },
          ],
          variables: [
            { name: "id", customName: "workflow_id", type: "string" },
          ],
        },
      }),

      // 8. Email workflow blueprint to requester
      emailNode(
        "8",
        "Email workflow blueprint",
        1720,
        "{requester_email}",
        "Workflow Ready: {workflow_goal}",
        "<h2>Your Workflow Blueprint</h2><p><strong>Goal:</strong> {workflow_goal}</p><p><strong>Nodes:</strong> {node_count} | <strong>Build Time:</strong> {estimated_build_time}</p><h3>Node List</h3><p>{node_list}</p><h3>Data Flow</h3><p>{data_flow}</p><h3>Integrations Required</h3><p>{api_integrations}</p><h3>Condition Logic</h3><p>{condition_logic}</p><h3>Error Handling</h3><p>{error_handling}</p><p><strong>Workflow ID:</strong> {workflow_id}</p>",
      ),

      // 9. Notify failures back to requester
      emailNode(
        "9",
        "Email: design issues found",
        1240,
        "{requester_email}",
        "Workflow Design Issues: {workflow_goal}",
        "<h2>Workflow Design Needs Revision</h2><p><strong>Goal:</strong> {workflow_goal}</p><p><strong>Issues Found:</strong> {issue_count}</p><p>{issues_found}</p><h3>Suggestions</h3><p>{suggestions}</p><p>Please revise your request and resubmit.</p>",
        460,
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"), // valid -> admin approval
      e("5", "9", "output_2"), // invalid -> email issues
      e("6", "7"),
      e("7", "8"),
    ],
    summary: `**Agentic Workflow Builder** — Natural language to validated workflow configuration with multi-step AI pipeline.\n\n1. User describes desired workflow in natural language\n2. **AI extracts** requirements: goal, trigger, steps, integrations, conditions\n3. **AI designs** architecture: node types, edges, data flow, error handling\n4. **AI validates** design for data dependencies, loops, unreachable nodes\n5. Valid designs go to **admin approval**\n6. Approved workflows **saved via API** with generated configuration\n7. Blueprint **emailed** to requester\n8. Invalid designs get **revision feedback** email\n\n**To activate:** Say "connect openai", "connect email"`,
  },

  // ===================================================================
  // 93. APP INACTIVE 30 DAYS (7 nodes)
  // ===================================================================

  "App Inactive 30 Days": {
    nodes: [
      trigger("Schedule: Check for 30-day inactive users", "schedule", {
        schedule: "Every day at 10:00 AM",
        howItStarts:
          "Runs daily to identify app users who have been inactive for 30 days and re-engage them",
        setup:
          "Activate the workflow. Connect your app analytics or user database API to identify inactive users.",
        inputFields: [],
      }),

      // 2. Fetch inactive users from analytics
      n("2", "hitApi", "Fetch 30-day inactive users", 280, {
        hitApiDetails: {
          method: "POST",
          url: "https://{app_domain}/api/v1/users/inactive",
          headers: [
            { key: "Authorization", value: "Bearer {api_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "inactive_days", value: "30" },
            { key: "limit", value: "50" },
          ],
          variables: [
            { name: "users", customName: "inactive_users", type: "string" },
            { name: "count", customName: "inactive_count", type: "number" },
          ],
        },
      }),

      // 3. AI personalize re-engagement message
      aiNode(
        "3",
        "AI craft re-engagement message",
        520,
        "You are a user retention specialist. Write a warm, personalized re-engagement email for a user who has been inactive for 30 days. Reference new features, offer value, and create gentle urgency. Do not be pushy. Respond JSON: {subject_line: string, email_body: string, cta_text: string, offer: string, new_features_highlight: string}",
        "Inactive users: {inactive_users}\\nInactive period: 30 days",
        [
          { field: "subject_line", as: "subject_line", type: "string" },
          { field: "email_body", as: "email_body", type: "string" },
          { field: "cta_text", as: "cta_text", type: "string" },
          { field: "offer", as: "offer", type: "string" },
          {
            field: "new_features_highlight",
            as: "new_features_highlight",
            type: "string",
          },
        ],
      ),

      // 4. Check if there are inactive users
      n("4", "condition", "Any inactive users found?", 760, {
        conditionData: [
          {
            variable: "inactive_count",
            value: "0",
            comparison_operator: "GREATER THAN",
            logical_operator: "AND",
          },
        ],
      }),

      // 5. Send re-engagement email via SendGrid
      n("5", "hitApi", "Send re-engagement email via SendGrid", 1000, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.sendgrid.com/v3/mail/send",
          headers: [
            { key: "Authorization", value: "Bearer {sendgrid_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "personalizations",
              value:
                '[{"to":[{"email":"{user_email}"}],"subject":"{subject_line}"}]',
            },
            {
              key: "from",
              value: '{"email":"{sender_email}","name":"{app_name}"}',
            },
            {
              key: "content",
              value: '[{"type":"text/html","value":"{email_body}"}]',
            },
            {
              key: "tracking_settings",
              value:
                '{"open_tracking":{"enable":true},"click_tracking":{"enable":true}}',
            },
          ],
          variables: [
            { name: "statusCode", customName: "send_status", type: "number" },
          ],
        },
      }),

      // 6. Push notification as secondary channel
      n("6", "pushNotification", "Push: We miss you!", 1240, {
        pushNotification: {
          title: "We miss you, {user_name}!",
          body: "{new_features_highlight} - Come check it out!",
        },
      }),

      // 7. Log re-engagement metrics
      n("7", "hitApi", "Log re-engagement campaign metrics", 1480, {
        hitApiDetails: {
          method: "POST",
          url: "https://sheets.googleapis.com/v4/spreadsheets/{sheet_id}/values/Reengagement!A1:append?valueInputOption=USER_ENTERED",
          headers: [
            { key: "Authorization", value: "Bearer {google_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "values",
              value:
                '[["30-day","{inactive_count}","{subject_line}","{offer}"]]',
            },
          ],
          variables: [
            {
              name: "updates.updatedRows",
              customName: "rows_logged",
              type: "number",
            },
          ],
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"), // inactive users found -> send email
      e("5", "6"),
      e("6", "7"),
    ],
    summary: `**App Inactive 30 Days** — Re-engage users who have not used your app for 30 days with personalized outreach.\n\n1. Runs **daily** to check for 30-day inactive users\n2. **App API** fetches inactive user list\n3. **AI crafts** personalized re-engagement email with new features and offers\n4. Only proceeds if inactive users exist\n5. **SendGrid sends** re-engagement email with tracking\n6. **Push notification** as secondary touchpoint\n7. Campaign metrics **logged to Google Sheets**\n\n**To activate:** Say "connect openai", "connect sendgrid", "connect google sheets"`,
  },

  // ===================================================================
  // 94. APP INACTIVE 60 DAYS (7 nodes)
  // ===================================================================

  "App Inactive 60 Days": {
    nodes: [
      trigger("Schedule: Check for 60-day inactive users", "schedule", {
        schedule: "Every day at 11:00 AM",
        howItStarts:
          "Runs daily to identify users inactive for 60 days and attempt final re-engagement before churn",
        setup:
          "Activate the workflow. Connect your user database API. This is the final outreach before marking a user as churned.",
        inputFields: [],
      }),

      // 2. Fetch 60-day inactive users
      n("2", "hitApi", "Fetch 60-day inactive users", 280, {
        hitApiDetails: {
          method: "POST",
          url: "https://{app_domain}/api/v1/users/inactive",
          headers: [
            { key: "Authorization", value: "Bearer {api_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "inactive_days", value: "60" },
            { key: "limit", value: "50" },
          ],
          variables: [
            { name: "users", customName: "inactive_users", type: "string" },
            { name: "count", customName: "inactive_count", type: "number" },
          ],
        },
      }),

      // 3. AI write final outreach with special offer
      aiNode(
        "3",
        "AI craft last-chance re-engagement",
        520,
        "You are a retention specialist. This is the FINAL re-engagement attempt for users inactive 60 days. Write a heartfelt message that includes: an exclusive comeback offer (discount/extended trial), what they are missing, a simple one-click reactivation path. Be empathetic, not desperate. Respond JSON: {subject_line: string, email_body: string, special_offer: string, cta_text: string, win_back_hook: string}",
        "Inactive users: {inactive_users}\\nInactive period: 60 days\\nThis is the final outreach before marking as churned.",
        [
          { field: "subject_line", as: "subject_line", type: "string" },
          { field: "email_body", as: "email_body", type: "string" },
          { field: "special_offer", as: "special_offer", type: "string" },
          { field: "cta_text", as: "cta_text", type: "string" },
          { field: "win_back_hook", as: "win_back_hook", type: "string" },
        ],
      ),

      // 4. Check if inactive users exist
      n("4", "condition", "Inactive users found?", 760, {
        conditionData: [
          {
            variable: "inactive_count",
            value: "0",
            comparison_operator: "GREATER THAN",
            logical_operator: "AND",
          },
        ],
      }),

      // 5. Send last-chance email
      n("5", "hitApi", "Send last-chance email via SendGrid", 1000, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.sendgrid.com/v3/mail/send",
          headers: [
            { key: "Authorization", value: "Bearer {sendgrid_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "personalizations",
              value:
                '[{"to":[{"email":"{user_email}"}],"subject":"{subject_line}"}]',
            },
            {
              key: "from",
              value: '{"email":"{sender_email}","name":"{app_name}"}',
            },
            {
              key: "content",
              value: '[{"type":"text/html","value":"{email_body}"}]',
            },
            {
              key: "tracking_settings",
              value:
                '{"open_tracking":{"enable":true},"click_tracking":{"enable":true}}',
            },
          ],
          variables: [
            { name: "statusCode", customName: "send_status", type: "number" },
          ],
        },
      }),

      // 6. Send SMS as urgent secondary channel
      n("6", "sendSMS", "SMS: Final re-engagement nudge", 1240, {
        sendSMSDetails: {
          to: "{user_phone}",
          smsBody:
            "We miss you, {user_name}! {special_offer} - Come back: {app_url}/reactivate",
        },
      }),

      // 7. Log churn risk and notify team
      slackNode(
        "7",
        "Notify #retention: 60-day churn risk",
        1480,
        "60-day inactive campaign sent to {inactive_count} users\\nOffer: {special_offer}\\nThese users will be marked churned if no response in 14 days.",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"), // users found -> send email
      e("5", "6"),
      e("6", "7"),
    ],
    summary: `**App Inactive 60 Days** — Final re-engagement attempt with special offers before marking users as churned.\n\n1. Runs **daily** to find 60-day inactive users\n2. **App API** fetches inactive user list\n3. **AI crafts** last-chance message with exclusive comeback offer\n4. Only proceeds if inactive users found\n5. **SendGrid sends** final re-engagement email with tracking\n6. **SMS sent** as urgent secondary channel\n7. **Slack notification** to retention team with churn risk count\n\n**To activate:** Say "connect openai", "connect sendgrid", "connect slack"`,
  },

  // ===================================================================
  // 95. APP INACTIVE 7 DAYS (6 nodes)
  // ===================================================================

  "App Inactive 7 Days": {
    nodes: [
      trigger("Schedule: Check for 7-day inactive users", "schedule", {
        schedule: "Every day at 9:00 AM",
        howItStarts:
          "Runs daily to catch users who stopped using the app within the first week (early churn signal)",
        setup:
          "Activate the workflow. Connect your user analytics API. This targets the critical 7-day drop-off window.",
        inputFields: [],
      }),

      // 2. Fetch 7-day inactive users
      n("2", "hitApi", "Fetch 7-day inactive users", 280, {
        hitApiDetails: {
          method: "POST",
          url: "https://{app_domain}/api/v1/users/inactive",
          headers: [
            { key: "Authorization", value: "Bearer {api_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "inactive_days", value: "7" },
            { key: "limit", value: "100" },
          ],
          variables: [
            { name: "users", customName: "inactive_users", type: "string" },
            { name: "count", customName: "inactive_count", type: "number" },
          ],
        },
      }),

      // 3. AI craft early re-engagement nudge
      aiNode(
        "3",
        "AI craft gentle re-engagement",
        520,
        "You are a customer success specialist. Write a friendly check-in email for users who have not logged in for 7 days. Focus on value: suggest a quick-win action they can take in under 5 minutes, highlight a popular feature, and offer help. Keep it short and personal. Respond JSON: {subject_line: string, email_body: string, quick_win_action: string, featured_tip: string}",
        "Inactive users: {inactive_users}\\nInactive period: 7 days (early churn signal)",
        [
          { field: "subject_line", as: "subject_line", type: "string" },
          { field: "email_body", as: "email_body", type: "string" },
          { field: "quick_win_action", as: "quick_win_action", type: "string" },
          { field: "featured_tip", as: "featured_tip", type: "string" },
        ],
      ),

      // 4. Check if users exist
      n("4", "condition", "Inactive users found?", 760, {
        conditionData: [
          {
            variable: "inactive_count",
            value: "0",
            comparison_operator: "GREATER THAN",
            logical_operator: "AND",
          },
        ],
      }),

      // 5. Send gentle nudge email
      n("5", "hitApi", "Send nudge email via SendGrid", 1000, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.sendgrid.com/v3/mail/send",
          headers: [
            { key: "Authorization", value: "Bearer {sendgrid_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "personalizations",
              value:
                '[{"to":[{"email":"{user_email}"}],"subject":"{subject_line}"}]',
            },
            {
              key: "from",
              value: '{"email":"{sender_email}","name":"{app_name}"}',
            },
            {
              key: "content",
              value: '[{"type":"text/html","value":"{email_body}"}]',
            },
            {
              key: "tracking_settings",
              value: '{"open_tracking":{"enable":true}}',
            },
          ],
          variables: [
            { name: "statusCode", customName: "send_status", type: "number" },
          ],
        },
      }),

      // 6. Log campaign
      n("6", "hitApi", "Log 7-day campaign metrics", 1240, {
        hitApiDetails: {
          method: "POST",
          url: "https://sheets.googleapis.com/v4/spreadsheets/{sheet_id}/values/Reengagement!A1:append?valueInputOption=USER_ENTERED",
          headers: [
            { key: "Authorization", value: "Bearer {google_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "values",
              value:
                '[["7-day","{inactive_count}","{subject_line}","{quick_win_action}"]]',
            },
          ],
          variables: [
            {
              name: "updates.updatedRows",
              customName: "rows_logged",
              type: "number",
            },
          ],
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"), // users found -> send email
      e("5", "6"),
    ],
    summary: `**App Inactive 7 Days** — Early churn detection with gentle re-engagement nudge.\n\n1. Runs **daily** to catch 7-day inactive users (critical drop-off window)\n2. **App API** fetches inactive user list\n3. **AI crafts** friendly check-in with quick-win action and feature tip\n4. Only sends if inactive users found\n5. **SendGrid sends** nudge email with open tracking\n6. Campaign **logged to Google Sheets**\n\n**To activate:** Say "connect openai", "connect sendgrid", "connect google sheets"`,
  },

  // ===================================================================
  // 96. APP INSTALL NO REGISTRATION (7 nodes)
  // ===================================================================

  "App Install No Registration": {
    nodes: [
      trigger("Schedule: Check unregistered installs daily", "schedule", {
        schedule: "Every day at 10:00 AM",
        howItStarts:
          "Runs daily to find users who installed the app but never completed registration",
        setup:
          "Activate the workflow. Connect your app analytics to identify installs without corresponding registrations.",
        inputFields: [],
      }),

      // 2. Fetch unregistered installs
      n("2", "hitApi", "Fetch installs without registration", 280, {
        hitApiDetails: {
          method: "POST",
          url: "https://{app_domain}/api/v1/users/unregistered-installs",
          headers: [
            { key: "Authorization", value: "Bearer {api_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "min_days_since_install", value: "1" },
            { key: "max_days_since_install", value: "7" },
            { key: "limit", value: "100" },
          ],
          variables: [
            { name: "users", customName: "unregistered_users", type: "string" },
            { name: "count", customName: "unregistered_count", type: "number" },
          ],
        },
      }),

      // 3. AI craft registration nudge
      aiNode(
        "3",
        "AI craft registration nudge",
        520,
        "You are an onboarding specialist. Write a push notification and in-app message to encourage users who installed but never registered. Highlight the key benefit they are missing, make registration feel quick (under 30 seconds), and offer an incentive. Respond JSON: {push_title: string, push_body: string, in_app_message: string, incentive: string, key_benefit: string}",
        "Unregistered installs: {unregistered_count}\\nUsers installed but never completed registration.",
        [
          { field: "push_title", as: "push_title", type: "string" },
          { field: "push_body", as: "push_body", type: "string" },
          { field: "in_app_message", as: "in_app_message", type: "string" },
          { field: "incentive", as: "incentive", type: "string" },
          { field: "key_benefit", as: "key_benefit", type: "string" },
        ],
      ),

      // 4. Check if unregistered users exist
      n("4", "condition", "Unregistered installs found?", 760, {
        conditionData: [
          {
            variable: "unregistered_count",
            value: "0",
            comparison_operator: "GREATER THAN",
            logical_operator: "AND",
          },
        ],
      }),

      // 5. Send push notification
      n("5", "pushNotification", "Push: Complete registration", 1000, {
        pushNotification: {
          title: "{push_title}",
          body: "{push_body}",
        },
      }),

      // 6. Trigger in-app message via API
      n("6", "hitApi", "Trigger in-app message", 1240, {
        hitApiDetails: {
          method: "POST",
          url: "https://{app_domain}/api/v1/in-app-messages/send",
          headers: [
            { key: "Authorization", value: "Bearer {api_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "target_segment", value: "unregistered_installs" },
            { key: "message", value: "{in_app_message}" },
            { key: "cta_url", value: "/register" },
            { key: "incentive", value: "{incentive}" },
          ],
          variables: [
            {
              name: "delivered_count",
              customName: "messages_delivered",
              type: "number",
            },
          ],
        },
      }),

      // 7. Log campaign metrics
      n("7", "hitApi", "Log unregistered install campaign", 1480, {
        hitApiDetails: {
          method: "POST",
          url: "https://sheets.googleapis.com/v4/spreadsheets/{sheet_id}/values/Registration!A1:append?valueInputOption=USER_ENTERED",
          headers: [
            { key: "Authorization", value: "Bearer {google_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "values",
              value:
                '[["unregistered_installs","{unregistered_count}","{messages_delivered}","{incentive}","{key_benefit}"]]',
            },
          ],
          variables: [
            {
              name: "updates.updatedRows",
              customName: "rows_logged",
              type: "number",
            },
          ],
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"), // users found -> push notification
      e("5", "6"),
      e("6", "7"),
    ],
    summary: `**App Install No Registration** — Convert app installs into registered users with targeted nudges.\n\n1. Runs **daily** to find users who installed but never registered (1-7 days)\n2. **App API** fetches unregistered install list\n3. **AI crafts** push notification and in-app message with registration incentive\n4. Only proceeds if unregistered users exist\n5. **Push notification** sent to nudge registration\n6. **In-app message** triggered with CTA and incentive\n7. Campaign **logged to Google Sheets**\n\n**To activate:** Say "connect openai", "connect google sheets"`,
  },

  // ===================================================================
  // 97. APP PREMIUM RENEWAL (7 nodes)
  // ===================================================================

  "App Premium Renewal": {
    nodes: [
      trigger("Schedule: Check upcoming premium renewals", "schedule", {
        schedule: "Every day at 8:00 AM",
        howItStarts:
          "Runs daily to identify premium subscriptions expiring in the next 7 days and manage renewal communications",
        setup:
          "Activate the workflow. Connect your billing API (Stripe, RevenueCat, or custom) to fetch upcoming renewals.",
        inputFields: [],
      }),

      // 2. Fetch upcoming renewals from Stripe
      n("2", "hitApi", "Fetch upcoming renewals from Stripe", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.stripe.com/v1/subscriptions?status=active&current_period_end[lte]={seven_days_from_now}&current_period_end[gte]={today_timestamp}&limit=50",
          headers: [
            { key: "Authorization", value: "Bearer {stripe_secret_key}" },
          ],
          variables: [
            { name: "data", customName: "renewals_data", type: "string" },
            {
              name: "data.length",
              customName: "renewal_count",
              type: "number",
            },
          ],
        },
      }),

      // 3. AI craft renewal communications
      aiNode(
        "3",
        "AI craft renewal communications",
        520,
        "You are a subscription retention specialist. For users with upcoming premium renewals, write: a renewal reminder email (positive, value-focused), a thank-you message for their loyalty, and highlight what they have gained during the current period. Respond JSON: {reminder_subject: string, reminder_body: string, loyalty_message: string, value_summary: string, renewal_cta: string}",
        "Upcoming renewals: {renewal_count}\\nRenewal data: {renewals_data}",
        [
          { field: "reminder_subject", as: "reminder_subject", type: "string" },
          { field: "reminder_body", as: "reminder_body", type: "string" },
          { field: "loyalty_message", as: "loyalty_message", type: "string" },
          { field: "value_summary", as: "value_summary", type: "string" },
          { field: "renewal_cta", as: "renewal_cta", type: "string" },
        ],
      ),

      // 4. Check if renewals exist
      n("4", "condition", "Renewals coming up?", 760, {
        conditionData: [
          {
            variable: "renewal_count",
            value: "0",
            comparison_operator: "GREATER THAN",
            logical_operator: "AND",
          },
        ],
      }),

      // 5. Send renewal reminder email
      n("5", "hitApi", "Send renewal reminder via SendGrid", 1000, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.sendgrid.com/v3/mail/send",
          headers: [
            { key: "Authorization", value: "Bearer {sendgrid_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "personalizations",
              value:
                '[{"to":[{"email":"{customer_email}"}],"subject":"{reminder_subject}"}]',
            },
            {
              key: "from",
              value: '{"email":"{sender_email}","name":"{app_name}"}',
            },
            {
              key: "content",
              value: '[{"type":"text/html","value":"{reminder_body}"}]',
            },
          ],
          variables: [
            { name: "statusCode", customName: "send_status", type: "number" },
          ],
        },
      }),

      // 6. Push notification reminder
      n("6", "pushNotification", "Push: Renewal reminder", 1240, {
        pushNotification: {
          title: "Premium renewal coming up!",
          body: "{loyalty_message} {renewal_cta}",
        },
      }),

      // 7. Log renewal campaign
      n("7", "hitApi", "Log renewal communications", 1480, {
        hitApiDetails: {
          method: "POST",
          url: "https://sheets.googleapis.com/v4/spreadsheets/{sheet_id}/values/Renewals!A1:append?valueInputOption=USER_ENTERED",
          headers: [
            { key: "Authorization", value: "Bearer {google_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "values",
              value:
                '[["premium_renewal","{renewal_count}","{reminder_subject}","{value_summary}"]]',
            },
          ],
          variables: [
            {
              name: "updates.updatedRows",
              customName: "rows_logged",
              type: "number",
            },
          ],
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"), // renewals found -> send reminder
      e("5", "6"),
      e("6", "7"),
    ],
    summary: `**App Premium Renewal** — Proactive renewal reminders for premium subscribers with value reinforcement.\n\n1. Runs **daily** to find premium subscriptions expiring in 7 days\n2. **Stripe API** fetches upcoming renewal list\n3. **AI crafts** value-focused renewal reminder with loyalty message\n4. Only proceeds if renewals are upcoming\n5. **SendGrid sends** renewal reminder email\n6. **Push notification** as secondary reminder\n7. Campaign **logged to Google Sheets**\n\n**To activate:** Say "connect openai", "connect stripe", "connect sendgrid", "connect google sheets"`,
  },

  // ===================================================================
  // 98. APP-INSTALL PROMPT AFTER PURCHASE (6 nodes)
  // ===================================================================

  "App-Install Prompt After Purchase": {
    nodes: [
      trigger("Webhook: Purchase completed", "webhook_app", {
        source: "Shopify / Stripe / E-commerce Platform",
        event: "order.completed",
        howItStarts:
          "A customer completes a purchase on your website but has not installed the mobile app",
        setup:
          "In Shopify -> Settings -> Notifications -> Webhooks -> Order payment, point to your workflow URL. Or connect your e-commerce platform webhook for completed orders.",
        inputFields: [],
      }),

      // 2. Extract purchase details
      aiNode(
        "2",
        "AI extract purchase + check app status",
        280,
        "Read this purchase webhook and extract: customer name, email, phone, order total, product names, and whether they are an existing app user. Look for app_installed or mobile_user fields. Respond JSON: {customer_name: string, customer_email: string, customer_phone: string, order_total: string, products: string, has_app: string}",
        "{webhook_payload}",
        [
          { field: "customer_name", as: "customer_name", type: "string" },
          { field: "customer_email", as: "customer_email", type: "string" },
          { field: "customer_phone", as: "customer_phone", type: "string" },
          { field: "order_total", as: "order_total", type: "string" },
          { field: "products", as: "products", type: "string" },
          { field: "has_app", as: "has_app", type: "string" },
        ],
      ),

      // 3. Check if customer has the app
      n("3", "condition", "Customer already has the app?", 520, {
        conditionData: [
          {
            variable: "has_app",
            value: "false",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 4. Send app install prompt email
      emailNode(
        "4",
        "Email: Download our app for order tracking",
        760,
        "{customer_email}",
        "Track your order on our app, {customer_name}!",
        "<h2>Thanks for your purchase, {customer_name}!</h2><p>Your order of {products} (${order_total}) is confirmed.</p><p><strong>Track your order in real-time</strong> by downloading our app:</p><ul><li>Live order tracking and delivery updates</li><li>Exclusive app-only deals and early access</li><li>Easy reordering with one tap</li><li>10% off your next purchase when you install</li></ul><p><a href='{app_store_url}'>Download for iOS</a> | <a href='{play_store_url}'>Download for Android</a></p>",
      ),

      // 5. Send SMS with app link
      n("5", "sendSMS", "SMS: App download link", 1000, {
        sendSMSDetails: {
          to: "{customer_phone}",
          smsBody:
            "Hi {customer_name}! Track your ${order_total} order live on our app + get 10% off next purchase: {app_download_link}",
        },
      }),

      // 6. Log app install prompt
      n("6", "hitApi", "Log install prompt metrics", 1240, {
        hitApiDetails: {
          method: "POST",
          url: "https://sheets.googleapis.com/v4/spreadsheets/{sheet_id}/values/AppInstalls!A1:append?valueInputOption=USER_ENTERED",
          headers: [
            { key: "Authorization", value: "Bearer {google_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "values",
              value:
                '[["{customer_name}","{customer_email}","{order_total}","{products}","post_purchase_prompt"]]',
            },
          ],
          variables: [
            {
              name: "updates.updatedRows",
              customName: "rows_logged",
              type: "number",
            },
          ],
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4", "output_1"), // no app -> send install prompt
      e("4", "5"),
      e("5", "6"),
    ],
    summary: `**App-Install Prompt After Purchase** — Convert web buyers into app users by prompting download after purchase.\n\n1. Purchase completed triggers webhook\n2. **AI extracts** customer details and checks app install status\n3. Only targets customers who have **not installed the app**\n4. **Email sent** with order tracking incentive and app download links\n5. **SMS sent** with direct app download link and 10% discount\n6. Install prompts **logged to Google Sheets**\n\n**To activate:** Say "connect openai", "connect email", "connect google sheets"`,
  },

  // ===================================================================
  // 99. APPOINTMENT REMINDER (7 nodes)
  // ===================================================================

  "Appointment Reminder": {
    nodes: [
      trigger("Schedule: Check upcoming appointments", "schedule", {
        schedule: "Every hour",
        howItStarts:
          "Runs hourly to find appointments happening in the next 24 hours and send reminders",
        setup:
          "Activate the workflow. Connect your calendar or booking system API (Calendly, Google Calendar, Acuity) to fetch upcoming appointments.",
        inputFields: [],
      }),

      // 2. Fetch upcoming appointments
      n("2", "hitApi", "Fetch appointments in next 24 hours", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.calendly.com/scheduled_events?min_start_time={now_iso}&max_start_time={tomorrow_iso}&status=active",
          headers: [
            { key: "Authorization", value: "Bearer {calendly_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          variables: [
            { name: "collection", customName: "appointments", type: "string" },
            {
              name: "collection.length",
              customName: "appointment_count",
              type: "number",
            },
          ],
        },
      }),

      // 3. AI generate personalized reminders
      aiNode(
        "3",
        "AI craft personalized reminders",
        520,
        "You are an appointment reminder assistant. For each upcoming appointment, create a friendly reminder with: the appointment type, date/time, location or video link, any prep instructions, and a reschedule option. Keep it concise and clear. Respond JSON: {reminder_subject: string, reminder_body: string, sms_reminder: string, prep_instructions: string, appointment_details: string}",
        "Appointments: {appointments}\\nReminder window: Next 24 hours",
        [
          { field: "reminder_subject", as: "reminder_subject", type: "string" },
          { field: "reminder_body", as: "reminder_body", type: "string" },
          { field: "sms_reminder", as: "sms_reminder", type: "string" },
          {
            field: "prep_instructions",
            as: "prep_instructions",
            type: "string",
          },
          {
            field: "appointment_details",
            as: "appointment_details",
            type: "string",
          },
        ],
      ),

      // 4. Check if appointments exist
      n("4", "condition", "Appointments found?", 760, {
        conditionData: [
          {
            variable: "appointment_count",
            value: "0",
            comparison_operator: "GREATER THAN",
            logical_operator: "AND",
          },
        ],
      }),

      // 5. Send email reminder
      emailNode(
        "5",
        "Send email appointment reminder",
        1000,
        "{attendee_email}",
        "{reminder_subject}",
        "<h2>Appointment Reminder</h2><p>{appointment_details}</p><p>{reminder_body}</p><h3>Preparation</h3><p>{prep_instructions}</p><p><a href='{reschedule_url}'>Need to reschedule?</a></p>",
      ),

      // 6. Send SMS reminder
      n("6", "sendSMS", "SMS appointment reminder", 1240, {
        sendSMSDetails: {
          to: "{attendee_phone}",
          smsBody: "{sms_reminder}",
        },
      }),

      // 7. Log reminders sent
      n("7", "hitApi", "Log reminder metrics", 1480, {
        hitApiDetails: {
          method: "POST",
          url: "https://sheets.googleapis.com/v4/spreadsheets/{sheet_id}/values/Reminders!A1:append?valueInputOption=USER_ENTERED",
          headers: [
            { key: "Authorization", value: "Bearer {google_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "values",
              value:
                '[["{appointment_count}","{reminder_subject}","email+sms"]]',
            },
          ],
          variables: [
            {
              name: "updates.updatedRows",
              customName: "rows_logged",
              type: "number",
            },
          ],
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"), // appointments found -> send reminders
      e("5", "6"),
      e("6", "7"),
    ],
    summary: `**Appointment Reminder** — Multi-channel appointment reminders with personalized prep instructions.\n\n1. Runs **every hour** to check for appointments in next 24 hours\n2. **Calendly API** fetches upcoming active appointments\n3. **AI crafts** personalized reminder with prep instructions and reschedule link\n4. Only sends if appointments are found\n5. **Email reminder** with full details and preparation notes\n6. **SMS reminder** as secondary touchpoint\n7. Reminders **logged to Google Sheets**\n\n**To activate:** Say "connect openai", "connect calendly", "connect email", "connect google sheets"`,
  },

  // ===================================================================
  // 100. APPOINTMENT OR BOOKING CONFIRMATION (6 nodes)
  // ===================================================================

  "Appointment or Booking Confirmation": {
    nodes: [
      trigger("Webhook: New appointment booked", "webhook_app", {
        source: "Calendly / Acuity / Google Calendar",
        event: "invitee.created",
        howItStarts:
          "A customer books an appointment or makes a reservation through your booking system",
        setup:
          "In Calendly -> Integrations -> Webhooks -> Subscribe to invitee.created events, point to your workflow URL. Or connect Acuity/Google Calendar webhooks.",
        inputFields: [],
      }),

      // 2. Extract booking details
      aiNode(
        "2",
        "AI extract booking details",
        280,
        "Read this booking webhook payload and extract: customer name, email, phone, appointment type, date and time, duration, location or video link, and any notes or questions from the customer. Different booking systems format data differently. Respond JSON: {customer_name: string, customer_email: string, customer_phone: string, appointment_type: string, date_time: string, duration: string, location: string, customer_notes: string}",
        "{webhook_payload}",
        [
          { field: "customer_name", as: "customer_name", type: "string" },
          { field: "customer_email", as: "customer_email", type: "string" },
          { field: "customer_phone", as: "customer_phone", type: "string" },
          { field: "appointment_type", as: "appointment_type", type: "string" },
          { field: "date_time", as: "date_time", type: "string" },
          { field: "duration", as: "duration", type: "string" },
          { field: "location", as: "location", type: "string" },
          { field: "customer_notes", as: "customer_notes", type: "string" },
        ],
      ),

      // 3. Send confirmation email
      emailNode(
        "3",
        "Send booking confirmation email",
        520,
        "{customer_email}",
        "Confirmed: {appointment_type} on {date_time}",
        "<h2>Booking Confirmed!</h2><p>Hi {customer_name},</p><p>Your appointment has been successfully booked. Here are the details:</p><table><tr><td><strong>Type:</strong></td><td>{appointment_type}</td></tr><tr><td><strong>Date & Time:</strong></td><td>{date_time}</td></tr><tr><td><strong>Duration:</strong></td><td>{duration}</td></tr><tr><td><strong>Location:</strong></td><td>{location}</td></tr></table><p>If you need to reschedule or cancel, please use the link in your calendar invite.</p><p>We look forward to seeing you!</p>",
      ),

      // 4. Send SMS confirmation
      n("4", "sendSMS", "SMS booking confirmation", 760, {
        sendSMSDetails: {
          to: "{customer_phone}",
          smsBody:
            "Confirmed: {appointment_type} on {date_time} at {location}. Duration: {duration}. Reply HELP to reschedule.",
        },
      }),

      // 5. Add to CRM / internal calendar
      n("5", "hitApi", "Add booking to CRM", 1000, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.hubapi.com/crm/v3/objects/contacts/search",
          headers: [
            { key: "Authorization", value: "Bearer {hubspot_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "filterGroups",
              value:
                '[{"filters":[{"propertyName":"email","operator":"EQ","value":"{customer_email}"}]}]',
            },
            { key: "properties", value: '["email","firstname","lastname"]' },
          ],
          variables: [
            { name: "total", customName: "contact_found", type: "number" },
            { name: "results[0].id", customName: "contact_id", type: "string" },
          ],
        },
      }),

      // 6. Notify team via Slack
      slackNode(
        "6",
        "Notify team: new booking",
        1240,
        "New booking confirmed!\\nCustomer: {customer_name} ({customer_email})\\nType: {appointment_type}\\nDate: {date_time}\\nDuration: {duration}\\nLocation: {location}\\nNotes: {customer_notes}",
      ),
    ],
    edges: [e("1", "2"), e("2", "3"), e("3", "4"), e("4", "5"), e("5", "6")],
    summary: `**Appointment or Booking Confirmation** — Multi-channel booking confirmation with CRM sync.\n\n1. Customer books appointment via Calendly, Acuity, or Google Calendar\n2. **AI extracts** booking details from webhook payload\n3. **Email confirmation** sent with full appointment details\n4. **SMS confirmation** sent as secondary channel\n5. **CRM lookup** to link booking to existing contact\n6. **Slack notification** to team with booking details and customer notes\n\n**To activate:** Say "connect openai", "connect email", "connect hubspot", "connect slack"`,
  },
};
